/* global bootstrap */ // tell ESLint that bootstrap is global

import { useParams, Link } from "react-router-dom";
import { getDatabase, ref, get, update, push, remove, set, runTransaction } from "firebase/database";
import { useEffect, useState } from "react";
import Navbar from "../components/NavBar";
import Footer from "../components/Footer";
import useTitle from "../components/Title";
import { getAuth } from "firebase/auth";
import { toast } from "../components/toast";
import { useNavigate } from "react-router-dom";
import { Slate, Editable, withReact } from 'slate-react';
import { createEditor, Editor, Transforms, Element as SlateElement } from 'slate';
import { withHistory } from 'slate-history';
import '../components/eventCreate/eventCreate.css';


function EventDetails() {
  const auth = getAuth();
  const database = getDatabase();
  const navigate = useNavigate();
  
  const [eventId, setEventId] = useState(null);
  const {eventUrlKey, eventSlug} = useParams();

  const [event, setEvent] = useState(null);
  const [eventOwner, setEventOwner] = useState(null);

  const [showEdit, setShowEdit] = useState(false);
  

  // Error states for validation
  const [eventNameError, setEventNameError] = useState("");
  const [eventPriceError, setEventPriceError] = useState("");
  const [eventLocationError, setEventLocationError] = useState("");
  const [eventPostcodeError, setEventPostcodeError] = useState("");

  // TOK state
  const [tokList, setTokList] = useState([]);
  const [newTokHeader, setNewTokHeader] = useState("");
  const [newTokPara, setNewTokPara] = useState("");
  const [newTokType, setNewTokType] = useState("");

  // FAQ state
  const [faqList, setFaqList] = useState([]);
  const [newQuestion, setNewQuestion] = useState("");
  const [newAnswer, setNewAnswer] = useState("");

  // Custom Pricing state
  const [pricingList, setPricingList] = useState([]);
  const [editingPricingId, setEditingPricingId] = useState(null);
  const [editPricingName, setEditPricingName] = useState("");
  const [editPricingMin, setEditPricingMin] = useState("");
  const [editPricingMax, setEditPricingMax] = useState("");
  const [editBestSeller, setEditBestSeller] = useState("");
  const [newPricingName, setNewPricingName] = useState("");
  const [newPricingMin, setNewPricingMin] = useState("");
  const [newPricingMax, setNewPricingMax] = useState("");
  const [newBestSeller, setNewBestSeller] = useState("");

  // Ticket Perks state (linked to pricing tiers)
  const [perksByPricingId, setPerksByPricingId] = useState({});
  const [editingPerkPricingId, setEditingPerkPricingId] = useState(null);
  const [editingPerkId, setEditingPerkId] = useState(null);
  const [editPerkName, setEditPerkName] = useState("");
  const [newPerkName, setNewPerkName] = useState("");
  const [newPerkPricingId, setNewPerkPricingId] = useState(null);

  // Schedule state
  const [scheduleList, setScheduleList] = useState([]);
  const [schedulesTitles, setSchedulesTitles] = useState([]); // Array of {id, name} for schedule titles
  const [newScheduleTitle, setNewScheduleTitle] = useState(""); // Create new schedule title
  const [selectedScheduleTitle, setSelectedScheduleTitle] = useState(""); // Selected schedule key for editing
  const [selectedPublicScheduleTab, setSelectedPublicScheduleTab] = useState(""); // Selected schedule key for public display
  const [newScheduleType, setNewScheduleType] = useState("");
  const [newScheduleStartHour, setNewScheduleStartHour] = useState("00");
  const [newScheduleStartMinute, setNewScheduleStartMinute] = useState("00");
  const [newScheduleHasEndTime, setNewScheduleHasEndTime] = useState(false);
  const [newScheduleEndHour, setNewScheduleEndHour] = useState("00");
  const [newScheduleEndMinute, setNewScheduleEndMinute] = useState("00");
  const [newScheduleLocation, setNewScheduleLocation] = useState("");

  const [selectedImage, setSelectedImage] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");

  // User groups for "Join with" functionality
  const [userGroups, setUserGroups] = useState([]); // Groups where user is OWNER/ADMIN (for modal)
  const [userJoinedGroups, setUserJoinedGroups] = useState([]); // All groups user is a member of (for button visibility)
  const [currentUser, setCurrentUser] = useState(null);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [addingEventToGroup, setAddingEventToGroup] = useState(false);
  const [eventGroupAssociations, setEventGroupAssociations] = useState({});
  
  // Track groups this event belongs to: { groupId, GROUP_PUBLIC, ATTENDING_COUNT_AT_JOIN }
  const [groupsAttendingTracking, setGroupsAttendingTracking] = useState({});

  // Reaction/Like/Dislike state
  const [userReaction, setUserReaction] = useState(null); // 'LIKE', 'DISLIKE', or null
  const [likeCount, setLikeCount] = useState(0);
  const [dislikeCount, setDislikeCount] = useState(0);

  // Slate editor state for editing - declare early so helper functions can use it
  const [editEditor] = useState(() => withHistory(withReact(createEditor())));
  const [editEditorValue, setEditEditorValue] = useState([
    {
      type: 'paragraph',
      children: [{ text: '' }],
    },
  ]);

  // Slate editor helper functions
  const toggleEditBlock = (format) => {
    const isActive = isEditBlockActive(editEditor, format);
    const isList = ['bulleted-list', 'numbered-list'].includes(format);

    Transforms.unwrapNodes(editEditor, {
      match: n => ['bulleted-list', 'numbered-list'].includes(SlateElement.isElement(n) ? n.type : ''),
      split: true,
    });

    let newProperties;
    if (isList) {
      newProperties = {
        type: isActive ? 'paragraph' : format,
      };
    } else {
      newProperties = {
        type: isActive ? 'paragraph' : format,
      };
    }
    Transforms.setNodes(editEditor, newProperties);
  };

  const toggleEditMark = (format) => {
    const isActive = isEditMarkActive(editEditor, format);
    if (isActive) {
      Editor.removeMark(editEditor, format);
    } else {
      Editor.addMark(editEditor, format, true);
    }
  };

  const isEditBlockActive = (editor, format) => {
    const { selection } = editor;
    if (!selection) return false;
    const [match] = Editor.nodes(editor, {
      match: n => SlateElement.isElement(n) && n.type === format,
    });
    return !!match;
  };

  const isEditMarkActive = (editor, format) => {
    const marks = Editor.marks(editor);
    return marks ? marks[format] === true : false;
  };

  const EditBlockButton = ({ format, label }) => {
    const isActive = isEditBlockActive(editEditor, format);
    return (
      <button
        onMouseDown={(e) => {
          e.preventDefault();
          toggleEditBlock(format);
        }}
        style={{
          backgroundColor: isActive ? '#FE5533' : 'var(--bg)',
        }}

        className="rich-edit-button"
        dangerouslySetInnerHTML={{ __html: label }}
      />
    );
  };

  const EditMarkButton = ({ format, label }) => {
    const isActive = isEditMarkActive(editEditor, format);
    return (
      <button
        onMouseDown={(e) => {
          e.preventDefault();
          toggleEditMark(format);
        }}
        style={{
          backgroundColor: isActive ? '#FE5533' : 'var(--bg)',
        }}
        className="rich-edit-button"
        dangerouslySetInnerHTML={{ __html: label }}
      />
    );
  };

  const toggleView = () => setShowEdit((prev) => !prev);

  // Preload editor value whenever event data loads (so data is ready before clicking Edit)
  useEffect(() => {
    if (event && event.EVENT_DESCRIPTION) {
      try {
        const parsed = JSON.parse(event.EVENT_DESCRIPTION);
        if (Array.isArray(parsed)) {
          setEditEditorValue(parsed);
        } else {
          setEditEditorValue([
            {
              type: 'paragraph',
              children: [{ text: event.EVENT_DESCRIPTION }],
            },
          ]);
        }
      } catch (error) {
        // Plain text fallback
        setEditEditorValue([
          {
            type: 'paragraph',
            children: [{ text: event.EVENT_DESCRIPTION }],
          },
        ]);
      }
    } else if (event && !event.EVENT_DESCRIPTION) {
      // Reset to empty if event has no description
      setEditEditorValue([
        {
          type: 'paragraph',
          children: [{ text: '' }],
        },
      ]);
    }
  }, [event]);

  const [currentUserId, setCurrentUserId] = useState(null);
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [currentUserDisplayName, setCurrentUserDisplayName] = useState(null);
  const [joinRequests, setJoinRequests] = useState([]);
  const [whitelistedUsers, setWhitelistedUsers] = useState([]);
  const [isUserWhitelisted, setIsUserWhitelisted] = useState(false);

  // Edit states for TOK and Schedule
  const [editingTokId, setEditingTokId] = useState(null);
  const [editTokHeader, setEditTokHeader] = useState("");
  const [editTokPara, setEditTokPara] = useState("");
  const [editTokType, setEditTokType] = useState("");

  const [editingScheduleId, setEditingScheduleId] = useState(null);
  const [editScheduleType, setEditScheduleType] = useState("");
  const [editScheduleStartHour, setEditScheduleStartHour] = useState("00");
  const [editScheduleStartMinute, setEditScheduleStartMinute] = useState("00");
  const [editScheduleHasEndTime, setEditScheduleHasEndTime] = useState(false);
  const [editScheduleEndHour, setEditScheduleEndHour] = useState("00");
  const [editScheduleEndMinute, setEditScheduleEndMinute] = useState("00");
  const [editScheduleLocation, setEditScheduleLocation] = useState("");

  const [editingFaqId, setEditingFaqId] = useState(null);
  const [editFaqQuestion, setEditFaqQuestion] = useState("");
  const [editFaqAnswer, setEditFaqAnswer] = useState("");

  // Join event states
  const [joinedEvents, setJoinedEvents] = useState({});

  const [backButton, setBackButton] = useState("");

  useEffect(() => {
    const auth = getAuth();

    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        setCurrentUserId(user.uid);
        setCurrentUser(user);

        const db = getDatabase();
        const userRef = ref(db, `USERS/${user.uid}`);
        const userSnap = await get(userRef);

        if (userSnap.exists()) {
          const userData = userSnap.val();
          setCurrentUserRole(userData.USERCLASS || null);
          setCurrentUserDisplayName(userData.USERDISPLAYNAME || null);
        }
      } else {
        setCurrentUserId(null);
        setCurrentUser(null);
        setCurrentUserRole(null);
        setCurrentUserDisplayName(null);
      }
    });

    return () => unsubscribe(); // cleanup listener
  }, []);

  // Fetch user's groups where they are owner or admin
  useEffect(() => {
    if (!currentUser) {
      // console.log("No currentUser, clearing groups");
      setUserGroups([]);
      setUserJoinedGroups([]);
      return;
    }

    // console.log("Fetching groups for user:", currentUser.uid);

    const fetchUserGroups = async () => {
      try {
        const db = getDatabase();
        // First, get the list of groups the user is in from USERS structure
        const userGroupsRef = ref(db, `USERS/${currentUser.uid}/GROUPS`);
        const userSnapshot = await get(userGroupsRef);

        // console.log("User groups snapshot exists:", userSnapshot.exists());

        if (userSnapshot.exists()) {
          const groupsData = userSnapshot.val();
          const adminGroups = [];
          const allJoinedGroups = [];

          // For each group, fetch the authoritative GROUP_RANK from GROUPS structure
          for (const groupId in groupsData) {
            const groupSlugData = groupsData[groupId];
            // console.log("groupSlugData:", groupSlugData);
            
            // Get the first slug (there should only be one)
            for (const slug in groupSlugData) {
              const userGroupInfo = groupSlugData[slug];
              // console.log("User group info from USERS:", userGroupInfo);

              // Now fetch the authoritative GROUP_RANK from GROUPS structure
              const groupRankRef = ref(db, `GROUPS/${groupId}/MEMBERS/${currentUser.uid}/GROUP_RANK`);
              const rankSnapshot = await get(groupRankRef);
              
              let actualGroupRank = userGroupInfo.GROUP_RANK;
              if (rankSnapshot.exists()) {
                actualGroupRank = rankSnapshot.val();
                // console.log("Fetched authoritative GROUP_RANK from GROUPS:", actualGroupRank);
              }

              const groupInfo = {
                ...userGroupInfo,
                GROUP_RANK: actualGroupRank,
              };

              // console.log("Final groupInfo with correct GROUP_RANK:", groupInfo);

              // Add to all joined groups
              allJoinedGroups.push({
                groupId,
                ...groupInfo,
              });

              // console.log("Pushed to allJoinedGroups, length now:", allJoinedGroups.length);

              // Check if user is owner or admin in this group
              if (actualGroupRank === "OWNER" || actualGroupRank === "ADMIN") {
                // console.log("User is OWNER or ADMIN with rank:", actualGroupRank);
                adminGroups.push({
                  groupId,
                  ...groupInfo,
                });
              } else {
                // console.log("User is not OWNER or ADMIN, GROUP_RANK is:", actualGroupRank);
              }
            }
          }

          // console.log("Admin groups:", adminGroups);
          // console.log("All joined groups:", allJoinedGroups);
          // console.log("About to set state - adminGroups length:", adminGroups.length, "allJoinedGroups length:", allJoinedGroups.length);

          setUserGroups(adminGroups);
          setUserJoinedGroups(allJoinedGroups);
        } else {
          // console.log("No groups found");
          setUserGroups([]);
          setUserJoinedGroups([]);
        }
      } catch (error) {
        console.error("Error fetching user groups:", error);
        setUserGroups([]);
        setUserJoinedGroups([]);
      }
    };

    fetchUserGroups();
  }, [currentUser]);

  // Log when userJoinedGroups changes
  useEffect(() => {
    // console.log("userJoinedGroups state updated:", userJoinedGroups);
    // console.log("userJoinedGroups length:", userJoinedGroups.length);
  }, [userJoinedGroups]);

  // Check which of the user's groups have this event
  useEffect(() => {
    if (!eventId || !selectedCategory || userGroups.length === 0) {
      setEventGroupAssociations({});
      return;
    }

    const checkGroupsForEvent = async () => {
      try {
        const db = getDatabase();
        const associations = {};

        for (const group of userGroups) {
          const eventRef = ref(db, `GROUPS/${group.groupId}/EVENTS/${selectedCategory}/${eventId}`);
          const snapshot = await get(eventRef);
          if (snapshot.exists()) {
            associations[group.groupId] = true;
          }
        }

        setEventGroupAssociations(associations);
      } catch (error) {
        console.error("Error checking event groups:", error);
        setEventGroupAssociations({});
      }
    };

    checkGroupsForEvent();
  }, [eventId, selectedCategory, userGroups]);


  // Fetch joined events based on selectedCategory
  useEffect(() => {
    if (!currentUserId || !selectedCategory) return;

    const db = getDatabase();
    const userRef = ref(db, `USERS/${currentUserId}/ATTENDING/${selectedCategory}`);

    const unsubscribe = get(userRef).then((snapshot) => {
      const data = snapshot.val() || {};
      // Map joined events by their IDs
      const joined = {};
      Object.values(data).forEach(item => {
        if (item.eventId) joined[item.eventId] = true;
      });
      setJoinedEvents(joined);
    });

    return () => unsubscribe;
  }, [currentUserId, selectedCategory]);

  // Toggle join/leave event function
  const toggleJoinRace = async (eventData) => {
    if (!currentUserId || !selectedCategory) return;

    const db = getDatabase();

    // User join/leave location
    const raceRef = ref(
      db,
      `USERS/${currentUserId}/ATTENDING/${selectedCategory}/${eventData.eventId}`
    );

    // Event attendee counter location
    const countRef = ref(
      db,
      `EVENTS/EVENT/${selectedCategory}/${eventData.eventId}/ATTENDING_COUNT`
    );

    try {
      // Read ONCE (important: avoids repeated triggers)
      const snapshot = await get(raceRef);
      const alreadyJoined = snapshot.exists();

      if (alreadyJoined) {
        // --- USER LEAVES ---
        await remove(raceRef);
        setJoinedEvents((prev) => ({
          ...prev,
          [eventData.eventId]: false,
        }));

        // Decrease attendee count safely
        await runTransaction(countRef, (current) => {
          return Math.max((current || 0) - 1, 0); // prevents negative
        });

      } else {
        // --- USER JOINS ---
        await set(raceRef, eventData);
        setJoinedEvents((prev) => ({
          ...prev,
          [eventData.eventId]: true,
        }));

        // Increase attendee count safely
        await runTransaction(countRef, (current) => {
          return (current || 0) + 1;
        });
      }
    } catch (error) {
      console.error("Error toggling event:", error);
    }
  };


  // console.log(event);

  const getGoogleMapsEmbed = (location, postcode) => {
  if (!location && !postcode) return null;

  const query = encodeURIComponent(
    [location, postcode].filter(Boolean).join(", ")
  );

  return `https://www.google.com/maps?q=${query}&output=embed`;
};

  const variableName = event?.EVENT_TYPE?.toLowerCase();

  const optionsMap = {
    race: [
      { value: "", label: "No Preset", img: "", photographer: "", photosite: "", photoevent: "" },
      { value: "0", label: "Race - Ieuan - Castle Combe Spectacular 2025", img: "/assets/img/RACE/race-1024.png", photographer: "Ieuan", photosite: "https://ieuanwilliamsphotography.myportfolio.com/", photoevent: "Castle Combe Spectacular 2025" },
      { value: "1", label: "Race - Ieuan - Castle Combe Spectacular 2025", img: "/assets/img/RACE/race1-1024.png", photographer: "Ieuan", photosite: "https://ieuanwilliamsphotography.myportfolio.com/", photoevent: "Castle Combe Spectacular 2025" },
      { value: "2", label: "Race - Ieuan - Castle Combe Spectacular 2025", img: "/assets/img/RACE/race2-1024.png", photographer: "Ieuan", photosite: "https://ieuanwilliamsphotography.myportfolio.com/", photoevent: "Castle Combe Spectacular 2025" },
    ],
    rally: [
      { value: "", label: "No Preset", img: "", photographer: "", photosite: "", photoevent: "" },
      { value: "0", label: "Rally - Jac - Nicky Grist Rally 2025", img: "/assets/img/RALLY/rally-1024.png", photographer: "Jac", photosite: "https://jacp228.myportfolio.com/", photoevent: "Nicky Grist Rally 2025" },
      { value: "1", label: "Rally - Jac - Nicky Grist Rally 2025", img: "/assets/img/RALLY/rally1-1024.png", photographer: "Jac", photosite: "https://jacp228.myportfolio.com/", photoevent: "Nicky Grist Rally 2025" },
      { value: "2", label: "Rally - Jac - Nicky Grist Rally 2025", img: "/assets/img/RALLY/rally2-1024.png", photographer: "Jac", photosite: "https://jacp228.myportfolio.com/", photoevent: "Nicky Grist Rally 2025" },
      { value: "3", label: "Rally - Jac - Nicky Grist Rally 2025", img: "/assets/img/RALLY/rally3-1024.png", photographer: "Jac", photosite: "https://jacp228.myportfolio.com/", photoevent: "Nicky Grist Rally 2025" },
      { value: "4", label: "Rally - Jac - Nicky Grist Rally 2025", img: "/assets/img/RALLY/rally4-1024.png", photographer: "Jac", photosite: "https://jacp228.myportfolio.com/", photoevent: "Nicky Grist Rally 2025" },
    ],
    trackday: [
      { value: "", label: "No Preset", img: "", photographer: "", photosite: "", photoevent: "" },
      { value: "0", label: "Trackday - Jac - JDM Action Day 2025", img: "/assets/img/TRACKDAY/trackday-1024.png", photosite: "https://jacp228.myportfolio.com/", photoevent: "JDM Action Day 2025" },
      { value: "1", label: "Trackday - Jac - Halloween Action Day 2025", img: "/assets/img/TRACKDAY/trackday1-1024.png", photographer: "Jac", photosite: "https://jacp228.myportfolio.com/", photoevent: "Halloween Action Day 2025" },
      { value: "3", label: "Trackday - Jac - Halloween Action Day 2025", img: "/assets/img/TRACKDAY/trackday3-1024.png", photographer: "Jac", photosite: "https://jacp228.myportfolio.com/", photoevent: "Halloween Action Day 2025" },
      { value: "4", label: "Trackday - Jac - JDM Action Day 2025", img: "/assets/img/TRACKDAY/trackday4-1024.png", photographer: "Jac", photosite: "https://jacp228.myportfolio.com/", photoevent: "JDM Action Day 2025" },
      { value: "6", label: "Trackday - Jac - JDM Action Day 2025", img: "/assets/img/TRACKDAY/trackday6-1024.png", photographer: "Jac", photosite: "https://jacp228.myportfolio.com/", photoevent: "JDM Action Day 2025" },
      { value: "7", label: "Trackday - Jac - Halloween Action Day 2025", img: "/assets/img/TRACKDAY/trackday7-1024.png", photographer: "Jac", photosite: "https://jacp228.myportfolio.com/", photoevent: "Halloween Action Day 2025" },
    ],
    meetup: [
      { value: "", label: "No Preset", img: "", photographer: "", photosite: "", photoevent: "" },
      { value: "0", label: "Meetup - Jac - South Wales Car Show 2025", img: "/assets/img/MEETUP/meetup-1024.png", photographer: "Jac", photosite: "https://jacp228.myportfolio.com/", photoevent: "South Wales Car Show 2025" },
      { value: "1", label: "Meetup - Jac - South Wales Car Show 2025", img: "/assets/img/MEETUP/meetup1-1024.png", photographer: "Jac", photosite: "https://jacp228.myportfolio.com/", photoevent: "South Wales Car Show 2025" },
      { value: "2", label: "Meetup - Jac - South Wales Car Show 2025", img: "/assets/img/MEETUP/meetup2-1024.png", photographer: "Jac", photosite: "https://jacp228.myportfolio.com/", photoevent: "South Wales Car Show 2025" },
      { value: "3", label: "Meetup - Jac - South Wales Car Show 2025", img: "/assets/img/MEETUP/meetup3-1024.png", photographer: "Jac", photosite: "https://jacp228.myportfolio.com/", photoevent: "South Wales Car Show 2025" },
      { value: "4", label: "Meetup - Jac - South Wales Car Show 2025", img: "/assets/img/MEETUP/meetup4-1024.png", photographer: "Jac", photosite: "https://jacp228.myportfolio.com/", photoevent: "South Wales Car Show 2025" },
      { value: "5", label: "Meetup - Jac - Cruise Culture 2025", img: "/assets/img/MEETUP/meetup5-1024.png", photographer: "Jac", photosite: "https://jacp228.myportfolio.com/", photoevent: "Cruise Culture 2025" },
    ],
    drift: [
      { value: "", label: "No Preset", img: "", photographer: "", photosite: "", photoevent: "" },
      { value: "0", label: "Drift - Jac - JDM Action Day 2025", img: "/assets/img/DRIFT/drift-1024.png", photographer: "Jac", photosite: "https://jacp228.myportfolio.com/", photoevent: "JDM Action Day 2025" },
      { value: "1", label: "Drift - Jac - JDM Action Day 2025", img: "/assets/img/DRIFT/drift1-1024.png", photographer: "Jac", photosite: "https://jacp228.myportfolio.com/", photoevent: "JDM Action Day 2025" },
      { value: "2", label: "Drift - Jac - JDM Action Day 2025", img: "/assets/img/DRIFT/drift2-1024.png", photographer: "Jac", photosite: "https://jacp228.myportfolio.com/", photoevent: "JDM Action Day 2025" },
      { value: "3", label: "Drift - Jac - JDM Action Day 2025", img: "/assets/img/DRIFT/drift3-1024.png", photographer: "Jac", photosite: "https://jacp228.myportfolio.com/", photoevent: "JDM Action Day 2025" },
      { value: "4", label: "Drift - Jac - JDM Action Day 2025", img: "/assets/img/DRIFT/drift4-1024.png", photographer: "Jac", photosite: "https://jacp228.myportfolio.com/", photoevent: "JDM Action Day 2025" },
    ],
    motorbike: [
      { value: "", label: "No Preset", img: "", photographer: "", photosite: "", photoevent: "" },
      { value: "0", label: "Motorbike - Rhys - unknown 2025", img: "/assets/img/MOTORBIKE/motorbike-1024.png", photographer: "Rhys", photosite: "", photoevent: "unknown" },
      { value: "1", label: "Motorbike - Rhys - unknown 2025", img: "/assets/img/MOTORBIKE/motorbike1-1024.png", photographer: "Rhys", photosite: "", photoevent: "unknown" },
    ],
    hockey: [
      { value: "", label: "No Preset", img: "", photographer: "", photosite: "", photoevent: "" },
      { value: "0", label: "Hockey - Jac - Hockey Game ", img: "/assets/img/HOCKEY/hockey-1024.png", photographer: "Jac", photosite: "https://jacp228.myportfolio.com/", photoevent: "Hockey Game 2025" },
      { value: "1", label: "Hockey - Jac - Hockey Game ", img: "/assets/img/HOCKEY/hockey1-1024.png", photographer: "Jac", photosite: "https://jacp228.myportfolio.com/", photoevent: "Hockey Game 2025" },
    ],
    football: [
      { value: "", label: "No Presets Available", img: "", photographer: "", photosite: "", photoevent: "" },
    ],
    basketball: [
      { value: "", label: "No Presets Available", img: "", photographer: "", photosite: "", photoevent: "" },
    ],
    swimming: [
      { value: "", label: "No Presets Available", img: "", photographer: "", photosite: "", photoevent: "" },
    ],
    drag: [
      { value: "", label: "No Presets Available", img: "", photographer: "", photosite: "", photoevent: "" },
    ],
    festival: [
      { value: "", label: "No Presets Available", img: "", photographer: "", photosite: "", photoevent: "" },
    ],
    rock: [
      { value: "", label: "No Presets Available", img: "", photographer: "", photosite: "", photoevent: "" },
    ],
    indie: [
      { value: "", label: "No Presets Available", img: "", photographer: "", photosite: "", photoevent: "" },
    ],
    jazz: [
      { value: "", label: "No Presets Available", img: "", photographer: "", photosite: "", photoevent: "" },
    ],
    dj: [
      { value: "", label: "No Presets Available", img: "", photographer: "", photosite: "", photoevent: "" },
    ],
    drumnbass: [
      { value: "", label: "No Presets Available", img: "", photographer: "", photosite: "", photoevent: "" },
    ],
    countryfolk: [
      { value: "", label: "No Presets Available", img: "", photographer: "", photosite: "", photoevent: "" },
    ],
    metal: [
      { value: "", label: "No Presets Available", img: "", photographer: "", photosite: "", photoevent: "" },
    ],
    pop: [
      { value: "", label: "No Presets Available", img: "", photographer: "", photosite: "", photoevent: "" },
    ],
    other: [
      { value: "", label: "Other", img: "/assets/img/OTHER/other.png", photographer: "Attendiau", photosite: "#", photoevent: "Attendiau" }
    ]
  };

  const options = optionsMap[variableName] || optionsMap["other"];
 
  const getEventFullEndingDate = (eventDate, extraDays = 0) => {
    if (!eventDate) return "";  
    const [dayStr, monthStr, yearStr] = eventDate.split("/");
    const day = Number(dayStr);
    const month = Number(monthStr) - 1;
    const year = Number(yearStr); 
    const dateObj = new Date(year, month, day);
    dateObj.setDate(dateObj.getDate() + Number(extraDays)); 
    const weekdays = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    const months = ["January","February","March","April","May","June","July","August","September","October","November","December"];
    const weekdayName = weekdays[dateObj.getDay()];
    const monthName = months[dateObj.getMonth()]; 
    const dayOfMonth = dateObj.getDate();
    const ordinal = (n) => { const s = ["th","st","nd","rd"]; const v = n % 100; return s[(v - 20) % 10] || s[v] || s[0]; };
    const dayWithOrdinal = `${dayOfMonth}${ordinal(dayOfMonth)}`; 
    // console.log("Full Ending Date:", `${weekdayName}, ${dayWithOrdinal} ${monthName} ${dateObj.getFullYear()}`);
    return `${weekdayName}, ${dayWithOrdinal} ${monthName} ${dateObj.getFullYear()}`;
  };
                    

  useEffect(() => {
  const fetchEvent = async () => {
    const db = getDatabase();
    const categories = ["AUTOMOTIVE", "SPORTS", "MUSIC"];

    try {
      for (const cat of categories) {
        const snapshot = await get(ref(db, `EVENTS/EVENT/${cat}/`));

        if (snapshot.exists()) {
          const events = snapshot.val();

          const foundEntry = Object.entries(events).find(
            ([_, event]) => event.EVENT_URLKEY === eventUrlKey && event.EVENT_SLUG === eventSlug
          );

          if (foundEntry) {
            const [eventId, foundEvent] = foundEntry;
            
            setEventId(eventId);    
            setEvent(foundEvent);
            setSelectedCategory(cat);
            setBackButton(`${cat}#event-card-${eventId}`);

            // FAQs
            if (foundEvent.FAQ) {
              setFaqList(
                Object.entries(foundEvent.FAQ).map(([id, data]) => ({ id, ...data }))
              );
            }

            // Schedule
            if (foundEvent.SCHEDULE) {
              const allItems = [];
              const titles = [];
              Object.entries(foundEvent.SCHEDULE).forEach(([scheduleKey, scheduleData]) => {
                titles.push({ id: scheduleKey, name: scheduleData.SCH_NAME });
                if (scheduleData.items) {
                  Object.entries(scheduleData.items).forEach(([itemKey, itemData]) => {
                    allItems.push({
                      id: itemKey,
                      parentScheduleId: scheduleKey,
                      ...itemData
                    });
                  });
                }
              });
              setScheduleList(allItems);
              setSchedulesTitles(titles);
            }

            // TOK
            if (foundEvent.TOK) {
              setTokList(Object.values(foundEvent.TOK));
            }

            return;
          }
        }
      }

      console.warn("No event found with urlKey:", eventUrlKey, "and slug:", eventSlug);
    } catch (error) {
      console.error("Error loading event:", error);
    } finally {
    
    }
  };

  fetchEvent();

    const closeMyModal = () => {
      const modalEl = document.getElementById("myModal");
      if (modalEl && window.bootstrap) {
        const modal = bootstrap.Modal.getInstance(modalEl) || new bootstrap.Modal(modalEl);
        modal.hide();
      }
      document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove());
      document.body.classList.remove("modal-open");
      document.body.style.overflow = "";
      document.body.style.removeProperty("padding-right");
    };

    const timer = setTimeout(closeMyModal, 1);
    return () => clearTimeout(timer);
}, [eventUrlKey, eventSlug]);

  // Scroll to top when event loads for view transition effect
  useEffect(() => {
    if (event) {
      window.scrollTo(0, 0);
    }
  }, [event]);
  
  // Load event owner details once the event is available
  useEffect(() => {
    const ownerId = event?.EVENT_OWNER;
    if (!ownerId) {
      setEventOwner(null);
      return;
    }

    const db = getDatabase();
    const ownerRef = ref(db, `USERS/${ownerId}`);

    get(ownerRef)
      .then((snap) => {
        if (snap.exists()) {
          // Include the ownerId alongside the user record
          // console.log(snap.val());
          setEventOwner({ 
          id: ownerId,...snap.val() 
          });
        } else {
          setEventOwner(null);
        }
      })
      .catch((err) => {
        // console.error("Failed to fetch event owner:", err);
        setEventOwner(null);
      });
  }, [event]);

  // Track traffic source on page load
  useEffect(() => {
    if (!eventId || !selectedCategory) return;

    const db = getDatabase();
    const eventRef = ref(db, `EVENTS/EVENT/${selectedCategory}/${eventId}`);
    const referrer = document.referrer;
    const currentDomain = window.location.hostname.toLowerCase();
    
    let fieldName;
    if (!referrer) {
      fieldName = 'EVENT_LOADED_SITE_EXTERNAL'; // Direct access, copy-paste, bookmarks
    } else {
      try {
        const referrerUrl = new URL(referrer);
        const referrerDomain = referrerUrl.hostname.toLowerCase();
        if (referrerDomain === currentDomain) {
          fieldName = 'EVENT_LOADED_SITE_FROM_PAGE'; // Only from within the site
        } else {
          fieldName = 'EVENT_LOADED_SITE_EXTERNAL'; // From external site
        }
      } catch (e) {
        fieldName = 'EVENT_LOADED_SITE_EXTERNAL';
      }
    }

    const newClickCount = (event[fieldName] || 0) + 1;
    update(eventRef, {
      [fieldName]: newClickCount,
    }).catch(() => {
      // Silent fail for tracking
    });
  }, [eventId, selectedCategory, event]);

  // Load whitelist for the event when it loads
  useEffect(() => {
    if (!eventId || !selectedCategory) return;

    const loadWhitelist = async () => {
      try {
        const db = getDatabase();
        const whitelistRef = ref(db, `EVENTS/EVENT/${selectedCategory}/${eventId}/WHITELIST`);
        const whitelistSnap = await get(whitelistRef);

        if (whitelistSnap.exists()) {
          const allWhitelist = whitelistSnap.val();
          const eventWhitelist = Object.keys(allWhitelist)
            .map(key => ({ id: key, ...allWhitelist[key] }));
          
          setWhitelistedUsers(eventWhitelist);
        } else {
          setWhitelistedUsers([]);
        }
      } catch (err) {
        console.error("Failed to load whitelisted users:", err);
        setWhitelistedUsers([]);
      }
    };

    loadWhitelist();
  }, [eventId, selectedCategory]);

  // Load custom pricing tiers for the event
  useEffect(() => {
    if (!eventId || !selectedCategory) return;

    const loadPricing = async () => {
      try {
        const db = getDatabase();
        const pricingRef = ref(db, `EVENTS/EVENT/${selectedCategory}/${eventId}/PRICING`);
        const pricingSnap = await get(pricingRef);

        if (pricingSnap.exists()) {
          const pricingData = Object.keys(pricingSnap.val())
            .map(key => ({ ...pricingSnap.val()[key] }));
          setPricingList(pricingData);

          // Load perks for each pricing tier
          const perksData = {};
          for (const pricing of pricingData) {
            const perksRef = ref(db, `EVENTS/EVENT/${selectedCategory}/${eventId}/PRICING/${pricing.PRICING_ID}/PERKS`);
            const perksSnap = await get(perksRef);
            if (perksSnap.exists()) {
              perksData[pricing.PRICING_ID] = Object.keys(perksSnap.val())
                .map(key => ({ ...perksSnap.val()[key] }));
            } else {
              perksData[pricing.PRICING_ID] = [];
            }
          }
          setPerksByPricingId(perksData);
        } else {
          setPricingList([]);
        }
      } catch (err) {
        console.error("Failed to load pricing:", err);
        setPricingList([]);
      }
    };

    loadPricing();
  }, [eventId, selectedCategory]);


  // Load group attending tracking from Firebase
  useEffect(() => {
    if (!eventId || !selectedCategory) return;

    const loadGroupsAttending = async () => {
      try {
        const db = getDatabase();
        const groupsAttendingRef = ref(db, `EVENTS/EVENT/${selectedCategory}/${eventId}/GROUPS_ATTENDING`);
        const groupsSnap = await get(groupsAttendingRef);

        if (groupsSnap.exists()) {
          const groupsData = groupsSnap.val();
          const formattedGroups = {};
          
          for (const groupId in groupsData) {
            formattedGroups[groupId] = groupsData[groupId];
          }
          
          setGroupsAttendingTracking(formattedGroups);
        } else {
          setGroupsAttendingTracking({});
        }
      } catch (err) {
        console.error("Failed to load groups attending:", err);
        setGroupsAttendingTracking({});
      }
    };

    loadGroupsAttending();
  }, [eventId, selectedCategory]);

  // Load reactions (likes/dislikes) for the event
  useEffect(() => {
    if (!eventId || !selectedCategory) return;

    const loadReactions = async () => {
      try {
        const db = getDatabase();
        const reactionsRef = ref(db, `EVENTS/EVENT/${selectedCategory}/${eventId}/REACTIONS`);
        const reactionsSnap = await get(reactionsRef);

        let likes = 0;
        let dislikes = 0;
        let userReact = null;

        if (reactionsSnap.exists()) {
          const reactionsData = reactionsSnap.val();
          
          for (const userId in reactionsData) {
            const reaction = reactionsData[userId];
            if (reaction === 'LIKE') {
              likes++;
              if (currentUserId === userId) {
                userReact = 'LIKE';
              }
            } else if (reaction === 'DISLIKE') {
              dislikes++;
              if (currentUserId === userId) {
                userReact = 'DISLIKE';
              }
            }
          }
        }

        setLikeCount(likes);
        setDislikeCount(dislikes);
        setUserReaction(userReact);
      } catch (err) {
        console.error("Failed to load reactions:", err);
        setLikeCount(0);
        setDislikeCount(0);
        setUserReaction(null);
      }
    };

    loadReactions();
  }, [eventId, selectedCategory, currentUserId]);

      const [extraDays, setExtraDays] = useState(0);

      useEffect(() => {
        if (event?.EVENT_EXTRADAYS !== undefined) {
          setExtraDays(event.EVENT_EXTRADAYS);
        }
      }, [event]);

      // Yes no questuions
      const [soldOut, setSoldOut] = useState(event?.EVENT_SOLDOUT === "Y");

      useEffect(() => {
        if (event) {
          setSoldOut(event.EVENT_SOLDOUT === "Y");
        }
      }, [event]);

      const [Sponsored, setSponsored] = useState(event?.EVENT_SPONSORED === "Y");

      useEffect(() => {
        if (event) {
          setSponsored(event.EVENT_SPONSORED === "Y");
        }
      }, [event]);

      const [Verified, setVerified] = useState(event?.EVENT_VERIFIED === "Y");

      useEffect(() => {
        if (event) {
          setVerified(event.EVENT_VERIFIED === "Y");
        }
      }, [event]);

      const [WeRecommend, setWeRecommend] = useState(event?.EVENT_WERECOMMEND === "Y");

      useEffect(() => {
        if (event) {
          setWeRecommend(event.EVENT_WERECOMMEND === "Y");
        }
      }, [event]);

    const [RequestAccess, setRequestAccess] = useState(event?.EVENT_ATTENDBYJOINING === "Y");
      useEffect(() => {
        if (event) {
          setRequestAccess(event.EVENT_ATTENDBYJOINING === "Y");
        }
      }, [event]);

          const [DisplayRecommendations, setDisplayRecommendations] = useState(event?.EVENT_SHOWREACTIONS === "Y");
      useEffect(() => {
        if (event) {
          setDisplayRecommendations(event.EVENT_SHOWREACTIONS === "Y");
        }
      }, [event]);


    const [Public, setPublic] = useState(event?.EVENT_PUBLIC === "Y");
      useEffect(() => {
        if (event) {
          setPublic(event.EVENT_PUBLIC === "Y");
        }
      }, [event]);

    const [reportReason, setReportReason] = useState("");
    const [reportDescription, setReportDescription] = useState("");

    const handleReport = async (e) => {
      e.preventDefault();

      if (!auth.currentUser) {
        alert("You must be logged in to submit a report.");
        return;
      }

      try {
        const reportId = `RID_${Date.now()}_${Math.floor(Math.random() * 10000)}`;

        const report_data = {
          REPORT_EVENTNAME: event.EVENT_NAME,
          REPORT_EVENTID: eventId,
          REPORT_EVENTREASON: reportReason,
          REPORT_DESCRIPTION: reportDescription,
          REPORT_USER: auth.currentUser.uid, 
          REPORT_EVENTOWNERNAME: event.EVENT_OWNERNAME,
          REPORT_EVENTURL: event.EVENT_SLUG,
          REPORT_DATE: new Date().toISOString(),
          REPORT_ISOPEN: "Y",
        };

        await set(
          ref(database, `REPORTS/${reportId}`),
          report_data
        );

        const modal = window.bootstrap.Modal
          .getInstance(document.getElementById("ReportModal"));
        modal.hide();

        setReportReason("");
        setReportDescription("");
      } catch (err) {
        console.error("Report failed:", err);
      }
    };

    const handleRequestToJoin = async () => {
      if (!currentUserId) {
        toast("You must be logged in to request to join", "#", "warning");
        navigate("/login");
        return;
      }

      try {
        const requestId = `JRQ_${Date.now()}_${Math.floor(Math.random() * 10000)}`;
        const joinRequest = {
          JRQ_USERID: currentUserId,
          JRQ_USERNAME: currentUserDisplayName || "Unknown User",
          JRQ_EVENTID: eventId,
          JRQ_EVENTNAME: event.EVENT_NAME,
          JRQ_EVENTOWNER: event.EVENT_OWNER,
          JRQ_DATE: new Date().toISOString(),
          JRQ_STATUS: "PENDING",
        };

        await set(
          ref(database, `EVENTS/EVENT/${selectedCategory}/${eventId}/REQUESTEDTOJOIN/${requestId}`),
          joinRequest
        );

        toast("Request to join sent to event organizer!", "#", "success");
      } catch (err) {
        console.error("Failed to send join request:", err);
        toast("Failed to send join request. Please try again.", "#", "danger");
      }
    };

    const handleReactionClick = async (reactionType) => {
      if (!currentUserId) {
        toast("You must be logged in to recommend events", "#", "warning");
        return;
      }

      try {
        const db = getDatabase();
        const reactionRef = ref(db, `EVENTS/EVENT/${selectedCategory}/${eventId}/REACTIONS/${currentUserId}`);
        
        let newUserReaction = null;
        let newLikes = likeCount;
        let newDislikes = dislikeCount;

        // If clicking the same reaction, toggle it off
        if (userReaction === reactionType) {
          await remove(reactionRef);
          newUserReaction = null;
          
          if (reactionType === 'LIKE') {
            newLikes = Math.max(0, likeCount - 1);
          } else {
            newDislikes = Math.max(0, dislikeCount - 1);
          }
        } else {
          // Setting a new reaction
          await set(reactionRef, reactionType);
          newUserReaction = reactionType;

          // Update counts
          if (userReaction === 'LIKE') {
            newLikes = Math.max(0, likeCount - 1);
          } else if (userReaction === 'DISLIKE') {
            newDislikes = Math.max(0, dislikeCount - 1);
          }

          if (reactionType === 'LIKE') {
            newLikes = likeCount + 1;
          } else {
            newDislikes = dislikeCount + 1;
          }
        }

        setUserReaction(newUserReaction);
        setLikeCount(newLikes);
        setDislikeCount(newDislikes);

        // Show feedback
        if (newUserReaction === 'LIKE') {

        } else if (newUserReaction === 'DISLIKE') {

        } else {

        }
      } catch (err) {
        console.error("Failed to save reaction:", err);

      }
    };

    const loadJoinRequests = async () => {
      if (!eventId) return;

      try {
        const db = getDatabase();
        const requestsRef = ref(db, `EVENTS/EVENT/${selectedCategory}/${eventId}/REQUESTEDTOJOIN`);
        const requestsSnap = await get(requestsRef);

        if (requestsSnap.exists()) {
          const allRequests = requestsSnap.val();
          const eventRequests = Object.keys(allRequests)
            .filter(key => allRequests[key].JRQ_STATUS === "PENDING")
            .map(key => ({ id: key, ...allRequests[key] }));
          
          setJoinRequests(eventRequests);
        } else {
          setJoinRequests([]);
        }
      } catch (err) {
        console.error("Failed to load join requests:", err);
      }
    };

    const loadWhitelistedUsers = async () => {
      if (!eventId) return;

      try {
        const db = getDatabase();
        const whitelistRef = ref(db, `EVENTS/EVENT/${selectedCategory}/${eventId}/WHITELIST`);
        const whitelistSnap = await get(whitelistRef);

        if (whitelistSnap.exists()) {
          const allWhitelist = whitelistSnap.val();
          const eventWhitelist = Object.keys(allWhitelist)
            .map(key => ({ id: key, ...allWhitelist[key] }));
          
          setWhitelistedUsers(eventWhitelist);
        } else {
          setWhitelistedUsers([]);
        }
      } catch (err) {
        console.error("Failed to load whitelisted users:", err);
      }
    };

    const handleApproveRequest = async (requestId, userId, userName) => {
      try {
        const db = getDatabase();
        
        // Add to whitelist
        const whitelistId = `WL_${Date.now()}_${Math.floor(Math.random() * 10000)}`;
        await set(ref(db, `EVENTS/EVENT/${selectedCategory}/${eventId}/WHITELIST/${whitelistId}`), {
          WL_USERID: userId,
          WL_USERNAME: userName,
          WL_EVENTID: eventId,
          WL_DATE_APPROVED: new Date().toISOString(),
        });

        // Update request status
        await set(ref(db, `EVENTS/EVENT/${selectedCategory}/${eventId}/REQUESTEDTOJOIN/${requestId}/JRQ_STATUS`), "APPROVED");
        
        toast(`${userName} has been approved!`, "#", "success");
        loadJoinRequests();
        loadWhitelistedUsers();
      } catch (err) {
        console.error("Failed to approve request:", err);
        toast("Failed to approve request. Please try again.", "#", "danger");
      }
    };

    const handleRejectRequest = async (requestId, userName) => {
      try {
        const db = getDatabase();
        await set(ref(db, `EVENTS/EVENT/${selectedCategory}/${eventId}/REQUESTEDTOJOIN/${requestId}/JRQ_STATUS`), "REJECTED");
        
        loadJoinRequests();
      } catch (err) {
        console.error("Failed to reject request:", err);
      }
    };

    const handleRemoveFromWhitelist = async (whitelistId, userName) => {
      try {
        const db = getDatabase();
        await remove(ref(db, `EVENTS/EVENT/${selectedCategory}/${eventId}/WHITELIST/${whitelistId}`));
        
        loadWhitelistedUsers();
      } catch (err) {
        console.error("Failed to remove from whitelist:", err);
      }
    };

    // Check if current user is whitelisted
    useEffect(() => {
      if (currentUserId && whitelistedUsers.length > 0) {
        const isWhitelisted = whitelistedUsers.some(user => user.WL_USERID === currentUserId);
        setIsUserWhitelisted(isWhitelisted);
      } else {
        setIsUserWhitelisted(false);
      }
    }, [currentUserId, whitelistedUsers]);


  useTitle(`- ${event ? event.EVENT_NAME : "Loading..."}`);

  const handleCopyUrl = async () => {
    try {
      const url = window.location.href;
      await navigator.clipboard.writeText(url);
      // toast("Copied URL. ", "#", "success");

          if (!url) return;

        const db = getDatabase();
        const eventRef = ref(db, `EVENTS/EVENT/${selectedCategory}/${eventId}`);

        if (currentUserId) {
          // User is logged in
          const newClickCount = (event.EVENT_SHARE_SITE_CLICKS_LOGGED_IN || 0) + 1;
          update(eventRef, {
            EVENT_SHARE_SITE_CLICKS_LOGGED_IN: newClickCount,
          })
            .then(() => {
            })
            .catch(() => {
              // toast("Error tracking click", "#", "danger");
            });
        } else {
          // User is not logged in
          const newClickCount = (event.EVENT_SHARE_SITE_CLICKS_NOT_LOGGED_IN || 0) + 1;
          update(eventRef, {
            EVENT_SHARE_SITE_CLICKS_NOT_LOGGED_IN: newClickCount,
          })
            .then(() => {
            })
            .catch(() => {
              // toast("Error tracking click", "#", "danger");
            });
        }

    } catch (err) {
      console.error("Failed to copy: ", err);
      // toast("Error copying URL. <br> Please Retry.", "#", "danger");
    }
  };



    
  if (!event) return <p className="text-center py-5">Event not found.</p>;
  
  // Convert Slate JSON to readable text
  const convertSlateToJSX = (description) => {
    try {
      const parsed = JSON.parse(description);
      
      // Check if it's a Slate document (array of blocks)
      if (Array.isArray(parsed)) {
        return parsed.map((block, idx) => {
          if (!block.children || !Array.isArray(block.children)) return null;
          
          const children = block.children.map((child, cidx) => {
            let text = child.text || "";
            if (child.bold) text = <strong key={`${idx}-${cidx}-bold`}>{text}</strong>;
            if (child.italic) text = <em key={`${idx}-${cidx}-italic`}>{text}</em>;
            if (child.underline) text = <u key={`${idx}-${cidx}-under`}>{text}</u>;
            return text;
          });
          
          switch (block.type) {
            case "heading-one":
              return <h1 key={idx} className="text-header1" style={{ fontSize: '24px', fontWeight: 'bold', margin: '15px 0 10px 0' }}>{children}</h1>;
            case "heading-two":
              return <h2 key={idx} className="text-header2" style={{ fontSize: '20px', fontWeight: 'bold', margin: '12px 0 8px 0' }}>{children}</h2>;
            case "heading-three":
              return <h3 key={idx} className="text-header3" style={{ fontSize: '16px', fontWeight: 'bold', margin: '10px 0 6px 0' }}>{children}</h3>;
            case "bulleted-list":
              return <li key={idx} className="text-bulleted-list" style={{ marginLeft: '20px', listStyleType: 'disc' }}>{children}</li>;
            case "numbered-list":
              return <li key={idx} className="text-numbered-list" style={{ marginLeft: '20px', listStyleType: 'decimal' }}>{children}</li>;
            default:
              return <p key={idx} className="event-text" style={{ margin: '8px 0' }}>{children}</p>;
          }
        });
      }
      
      // If it's not a Slate document, return as plain text
      return description;
    } catch (error) {
      // If parsing fails, it's plain text
      return description;
    }
  };

  const ledescription = event.EVENT_DESCRIPTION 
    ? convertSlateToJSX(event.EVENT_DESCRIPTION)
    : "It appears that the event organizer has not yet provided a description.";


const imgNUM = event.EVENT_IMAGENUM || "";

const imageMap = {
  RACE: `race${imgNUM}`,
  RALLY: `rally${imgNUM}`,
  MEETUP: `meetup${imgNUM}`,
  DRAG: `drag${imgNUM}`,
  DRIFT: `drift${imgNUM}`,
  TRACKDAY: `trackday${imgNUM}`,
  MOTORBIKE: `motorbike${imgNUM}`,
  HOCKEY: `hockey${imgNUM}`,
  DEFAULT: `other${imgNUM}`,
};

const presetImage = imgNUM
  ? `/assets/img/${event.EVENT_TYPE.toLowerCase()}/${imageMap[event.EVENT_TYPE] || imageMap.DEFAULT}-1024.png`
  : null;

const customImage = event.EVENT_IMAGE
  ? `/assets/img/uploads/events/${event.EVENT_IMAGE}.png`
  : null;

const imageSrc = customImage || presetImage;

  const handleUpdateEvent = (eventId) => {
    setEventNameError("");
    setEventPriceError("");
    setEventLocationError("");
    setEventPostcodeError("");

    const editDay = document.getElementById(`EditEventDay-${eventId}`)?.value;
    const editMonth = document.getElementById(`EditEventMonth-${eventId}`)?.value;
    const editYear = document.getElementById(`EditEventYear-${eventId}`)?.value;
    const eventDatesEdit = `${editDay}/${editMonth}/${editYear}`;

    const editeventName = document.getElementById(`EditEventName-${eventId}`)?.value;
    if (!editeventName || editeventName.trim() === "") {
      setEventNameError("Event name is required.");
      return;
    }

    const editEventPrice = document.getElementById(`EditEventPrice-${eventId}`)?.value;
    if (!editEventPrice || editEventPrice.trim() === "") {
      setEventPriceError("Event Price is required. - 0 is free. TBC is unknown");
      return;
    }

    const editEventLocation = document.getElementById(`EditEventLocation-${eventId}`)?.value;
    if (!editEventLocation || editEventLocation.trim() === "") {
      setEventLocationError("Event Location is required. - TBC is unknown");
      return;
    }

    const editEventPostcode = document.getElementById(`EditEventPostcode-${eventId}`)?.value;

    if (!editEventPostcode || editEventPostcode.trim() === "") {
      setEventPostcodeError("Event Postcode is required. - TBC is unknown");
      return;
    }

    const editEventVerifiedChecked = document.getElementById(`EditEventVerified-${eventId}`)?.checked;
    const editEventVerified = editEventVerifiedChecked ? "Y" : "N";

    const editEventSponsoredChecked = document.getElementById(`EditEventSponsored-${eventId}`)?.checked;
    const editEventSponsored = editEventSponsoredChecked ? "Y" : "N";

    const editEventWeRecommendChecked = document.getElementById(`EditEventWeRecommend-${eventId}`)?.checked;
    const editEventWeRecommend = editEventWeRecommendChecked ? "Y" : "N";

    const editEventSoldOutChecked = document.getElementById(`EditEventSoldOut-${eventId}`)?.checked;
    const editEventSoldOut = editEventSoldOutChecked ? "Y" : "N";
    
    
    const editEventPriceMin = document.getElementById(`EditEventPriceMin-${eventId}`)?.value;
    const editEventExtraDays = document.getElementById(`editEventExtraDays-${eventId}`)?.value;

    const editEventRequestAccessChecked = document.getElementById(`EditEventRequestAccess-${eventId}`)?.checked;
    const editEventRequestAccess = editEventRequestAccessChecked ? "Y" : "N";

    const editEventPublicChecked = document.getElementById(`EditEventPublic-${eventId}`)?.checked;
    const editEventPublic = editEventPublicChecked ? "Y" : "N";

    const editEventReactionsChecked = document.getElementById(`EditEventDisplayRecommendations-${eventId}`)?.checked;
    const editEventReactions = editEventReactionsChecked ? "Y" : "N";

    const editEventPhotographyAllowed = document.getElementById(`EditEventPhotography-${eventId}`)?.value || "N";
    const editEventDronesAllowed = document.getElementById(`EditEventDrones-${eventId}`)?.value || "N";

    const updatedEvent = {
      EVENT_NAME: editeventName,
      EVENT_DATE: eventDatesEdit,
      EVENT_PRICE: editEventPrice,
      EVENT_SITE: document.getElementById(`EditEventSite-${eventId}`)?.value || "",
      EVENT_TICKETSITE: document.getElementById(`EditEventTicketSite-${eventId}`)?.value || "",
      EVENT_LOCATION: editEventLocation,
      EVENT_POSTCODE: editEventPostcode,
      EVENT_VERIFIED: editEventVerified, 
      EVENT_SPONSORED: editEventSponsored,
      EVENT_WERECOMMEND: editEventWeRecommend,
      EVENT_DESCRIPTION: JSON.stringify(editEditorValue),
      EVENT_IMAGENUM: selectedImage,
      EVENT_STARTTIME: `${document.getElementById(`EditEventStartHour-${eventId}`)?.value || "00"}:${document.getElementById(`EditEventStartMinute-${eventId}`)?.value || "00"}`,
      EVENT_ENDTIME: `${document.getElementById(`EditEventEndHour-${eventId}`)?.value || "00"}:${document.getElementById(`EditEventEndMinute-${eventId}`)?.value || "00"}`,
      EVENT_IMAGELOCATION: "BANNER",
      EVENT_SOLDOUT: editEventSoldOut,
      EVENT_PRICEMIN: editEventPriceMin,
      EVENT_EXTRADAYS: editEventExtraDays,
      EVENT_ATTENDBYJOINING: editEventRequestAccess,
      EVENT_PUBLIC: editEventPublic,
      EVENT_PHOTOGRAPHYALLOWED: editEventPhotographyAllowed,
      EVENT_DRONESALLOWED: editEventDronesAllowed,
      EVENT_SHOWREACTIONS: editEventReactions,
    }


    // console.log("Updated event data:", updatedEvent);

    const database = getDatabase();
    const eventRef = ref(database, `EVENTS/EVENT/${selectedCategory}/${eventId}`);
    import("firebase/database").then(({ update }) => {
      update(eventRef, updatedEvent)
        .then(() => {
          // console.log("Event updated successfully");
          setEvent((prev) => ({ ...prev, ...updatedEvent }));
          setShowEdit(false);
        })
        .catch((error) => {
          // console.error("Error updating event:", error);
        });
    });
    
    const now = new Date();
    const datetimenow = `${String(now.getDate()).padStart(2, "0")}/${String(now.getMonth() + 1).padStart(2, "0")}/${now.getFullYear()} : ${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
    eventAuditUpdate(selectedCategory, "UPDATED", editeventName, currentUserId, datetimenow, eventId, "Event Updated"); 

  };

    function eventAuditUpdate(category, action, eventName, userId, timestamp, eventId, description) {
      const database = getDatabase();
      const timestampMs = new Date().getTime();
      const auditRef = ref(database, "AUDIT/" + category + "/" + eventId + "/" + timestampMs);
      const auditData = {
        action,
        eventName,
        userId,
        timestamp,
        description
      };

      set(auditRef, auditData);
    };

  // Handle Backup
  const handleBackup = () => {
    if (!event || !currentUserId || !selectedCategory) return;

    const editeventName = document.getElementById(`EditEventName-${eventId}`)?.value || event.EVENT_NAME;
    const now = new Date();
    const datetimenow = `${String(now.getDate()).padStart(2, "0")}/${String(now.getMonth() + 1).padStart(2, "0")}/${now.getFullYear()} : ${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
    
    const backupJSON = JSON.stringify(event, null, 2);
    
    eventAuditUpdate(selectedCategory, "BACKUP", editeventName, currentUserId, datetimenow, eventId, backupJSON);
    // toast("Event data backed up successfully", "#", "success");
  };

  // Add TOK
const handleAddTok = () => {
  if (!newTokHeader.trim() || !newTokPara.trim()) return;

  const db = getDatabase();
  const tokRef = ref(db, `EVENTS/EVENT/${selectedCategory}/${eventId}/TOK`);
  const newTokKey = push(tokRef).key;

  const newTokItem = {
    id: newTokKey,   // add this
    HEADER: newTokHeader,
    PARA: newTokPara,
    TYPE: newTokType
  };

  update(ref(db, `EVENTS/EVENT/${selectedCategory}/${eventId}/TOK/${newTokKey}`), newTokItem)
    .then(() => {
      setTokList((prev) => [...prev, newTokItem]); // push with id
      setNewTokHeader("");
      setNewTokPara("");
      // toast("Things to Know added successfully", "#", "success");
    })
    .catch(() => toast("Things to know has errored!", "#", "danger")); 
};

const handleDeleteTok = (tokId) => {
  const db = getDatabase();
  const tokRef = ref(db, `EVENTS/EVENT/${selectedCategory}/${eventId}/TOK/${tokId}`);

  remove(tokRef)
    .then(() => {
      setTokList((prev) => prev.filter((item) => item.id !== tokId));
      // toast("Things to Know deleted successfully", "#", "success");
    })
    .catch(() => toast("Error deleting Things to Know", "#", "danger"));
};

const handleEditTok = (tok) => {
  setEditingTokId(tok.id);
  setEditTokHeader(tok.HEADER);
  setEditTokPara(tok.PARA);
  setEditTokType(tok.TYPE);
};

const handleSaveTok = (tokId) => {
  if (!editTokHeader.trim() || !editTokPara.trim()) {
    // toast("Header and message are required", "#", "warning");
    return;
  }

  const db = getDatabase();
  const tokRef = ref(db, `EVENTS/EVENT/${selectedCategory}/${eventId}/TOK/${tokId}`);

  update(tokRef, {
    HEADER: editTokHeader,
    PARA: editTokPara,
    TYPE: editTokType
  })
    .then(() => {
      setTokList((prev) =>
        prev.map((item) =>
          item.id === tokId
            ? { ...item, HEADER: editTokHeader, PARA: editTokPara, TYPE: editTokType }
            : item
        )
      );
      setEditingTokId(null);
      // toast("Things to Know updated successfully", "#", "success");
    })
    .catch(() => toast("Error updating Things to Know", "#", "danger"));
};

  // Add FAQ
  const handleAddFaq = () => {
    if (!newQuestion.trim()) return;

    const db = getDatabase();
    const faqRef = ref(db, `EVENTS/EVENT/${selectedCategory}/${eventId}/FAQ`);
    const newFaqKey = push(faqRef).key;

    const newFaqItem = { QUESTION: newQuestion, ANSWER: newAnswer };

        const now = new Date();
        const datetimenow = `${String(now.getDate()).padStart(2, "0")}/${String(now.getMonth() + 1).padStart(2, "0")}/${now.getFullYear()} : ${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
        eventFAQUpdateAudit(selectedCategory, "ADD FAQ", "", currentUserId, datetimenow, eventId, "Added FAQ: " + newQuestion); 

    update(ref(db, `EVENTS/EVENT/${selectedCategory}/${eventId}/FAQ/${newFaqKey}`), newFaqItem)
      .then(() => {
        setFaqList([...faqList, newFaqItem]);
        setNewQuestion("");
        setNewAnswer("");
        // toast("FAQ added successfully", "#", "success");
      })

      .catch(() => toast("FAQ Errored!", "#", "danger"));
  };

    function eventFAQUpdateAudit(category, action, eventName, userId, timestamp, eventId, description) {
      const database = getDatabase();
      const timestampMs = new Date().getTime();
      const auditRef = ref(database, "AUDIT/" + category + "/" + eventId + "/" + timestampMs);
      const auditData = {
        action,
        eventName,
        userId,
        timestamp,
        description
      };

      set(auditRef, auditData);
    };

  const handleDeleteFaq = (faqId) => {
    const db = getDatabase();
    const faqRef = ref(db, `EVENTS/EVENT/${selectedCategory}/${eventId}/FAQ/${faqId}`);

        const now = new Date();
        const datetimenow = `${String(now.getDate()).padStart(2, "0")}/${String(now.getMonth() + 1).padStart(2, "0")}/${now.getFullYear()} : ${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
        eventFAQDeleteAudit(selectedCategory, "DELETE FAQ", "", currentUserId, datetimenow, eventId, "Deleted FAQ: " + faqId); 

    remove(faqRef)
      .then(() => {
        setFaqList(faqList.filter((faq) => faq.id !== faqId));
        toast("FAQ deleted successfully", "#", "success");
      })
      .catch(() => toast("Error deleting FAQ", "#", "danger"));
  };

    function eventFAQDeleteAudit(category, action, eventName, userId, timestamp, eventId, description) {
      const database = getDatabase();
      const timestampMs = new Date().getTime();
      const auditRef = ref(database, "AUDIT/" + category + "/" + eventId + "/" + timestampMs);
      const auditData = {
        action,
        eventName,
        userId,
        timestamp,
        description
      };

      set(auditRef, auditData);
    };

  const handleEditFaq = (faq) => {
    setEditingFaqId(faq.id);
    setEditFaqQuestion(faq.QUESTION);
    setEditFaqAnswer(faq.ANSWER);
  };

  const handleSaveFaq = (faqId) => {
    if (!editFaqQuestion.trim() || !editFaqAnswer.trim()) return;

    const db = getDatabase();
    const faqRef = ref(db, `EVENTS/EVENT/${selectedCategory}/${eventId}/FAQ/${faqId}`);

      const now = new Date();
      const datetimenow = `${String(now.getDate()).padStart(2, "0")}/${String(now.getMonth() + 1).padStart(2, "0")}/${now.getFullYear()} : ${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
      eventFAEditedAudit(selectedCategory, "FAQ EDITED", "", currentUserId, datetimenow, eventId, "Edited FAQ: " + faqId); 
      

    update(faqRef, {
      QUESTION: editFaqQuestion,
      ANSWER: editFaqAnswer,
    })
      .then(() => {
        setFaqList(
          faqList.map((faq) =>
            faq.id === faqId
              ? { ...faq, QUESTION: editFaqQuestion, ANSWER: editFaqAnswer }
              : faq
          )
        );
        setEditingFaqId(null);
        setEditFaqQuestion("");
        setEditFaqAnswer("");
        toast("FAQ updated successfully", "#", "success");
      })
      .catch(() => toast("Error updating FAQ", "#", "danger"));
  };

    function eventFAEditedAudit(category, action, eventName, userId, timestamp, eventId, description) {
      const database = getDatabase();
      const timestampMs = new Date().getTime();
      const auditRef = ref(database, "AUDIT/" + category + "/" + eventId + "/" + timestampMs);
      const auditData = {
        action,
        eventName,
        userId,
        timestamp,
        description
      };

      set(auditRef, auditData);
    };

  // Custom Pricing Management
  const handleAddPricing = () => {
    if (!newPricingName.trim() || !newPricingMin.trim() || !newPricingMax.trim()) {
      toast("Please fill in all pricing fields", "#", "warning");
      return;
    }

    const db = getDatabase();
    const pricingRef = ref(db, `EVENTS/EVENT/${selectedCategory}/${eventId}/PRICING`);
    const newPricingKey = push(pricingRef).key;

    const newPricingItem = {
      PRICING_ID: newPricingKey,
      PRICING_NAME: newPricingName,
      PRICING_PRICEMIN: newPricingMin,
      PRICING_PRICEMAX: newPricingMax,
      PRICING_BESTSELLER: newBestSeller || "N",
    };

    update(ref(db, `EVENTS/EVENT/${selectedCategory}/${eventId}/PRICING/${newPricingKey}`), newPricingItem)
      .then(() => {
        setPricingList([...pricingList, newPricingItem]);
        setNewPricingName("");
        setNewPricingMin("");
        setNewPricingMax("");
        setNewBestSeller("");
        // toast("Pricing tier added successfully", "#", "success");
      })
      .catch(() => toast("Error adding pricing tier", "#", "danger"));
  };

  const handleEditPricing = (pricing) => {
    setEditingPricingId(pricing.PRICING_ID);
    setEditPricingName(pricing.PRICING_NAME);
    setEditPricingMin(pricing.PRICING_PRICEMIN);
    setEditPricingMax(pricing.PRICING_PRICEMAX);
    setEditBestSeller(pricing.PRICING_BESTSELLER || "N");
  };

  const handleSavePricing = (pricingId) => {
    if (!editPricingName.trim() || !editPricingMin.trim() || !editPricingMax.trim()) {
      toast("Please fill in all pricing fields", "#", "warning");
      return;
    }

    const db = getDatabase();
    const pricingRef = ref(db, `EVENTS/EVENT/${selectedCategory}/${eventId}/PRICING/${pricingId}`);

    update(pricingRef, {
      PRICING_NAME: editPricingName,
      PRICING_PRICEMIN: editPricingMin,
      PRICING_PRICEMAX: editPricingMax,
      PRICING_BESTSELLER: editBestSeller,
    })
      .then(() => {
        setPricingList(
          pricingList.map((pricing) =>
            pricing.PRICING_ID === pricingId
              ? {
                  ...pricing,
                  PRICING_NAME: editPricingName,
                  PRICING_PRICEMIN: editPricingMin,
                  PRICING_PRICEMAX: editPricingMax,
                  PRICING_BESTSELLER: editBestSeller,
                }
              : pricing
          )
        );
        setEditingPricingId(null);
        setEditPricingName("");
        setEditPricingMin("");
        setEditPricingMax("");
        toast("Pricing tier updated successfully", "#", "success");
      })
      .catch(() => toast("Error updating pricing tier", "#", "danger"));
  };

  const handleDeletePricing = (pricingId) => {
    const db = getDatabase();
    const pricingRef = ref(db, `EVENTS/EVENT/${selectedCategory}/${eventId}/PRICING/${pricingId}`);

    remove(pricingRef)
      .then(() => {
        setPricingList(pricingList.filter((pricing) => pricing.PRICING_ID !== pricingId));
        toast("Pricing tier deleted successfully", "#", "success");
      })
      .catch(() => toast("Error deleting pricing tier", "#", "danger"));
  };

  // Ticket Perks Management (linked to pricing tiers)
  const handleAddPerk = (pricingId) => {
    if (!newPerkName.trim()) {
      toast("Please enter a perk name", "#", "warning");
      return;
    }

    const db = getDatabase();
    const perksRef = ref(db, `EVENTS/EVENT/${selectedCategory}/${eventId}/PRICING/${pricingId}/PERKS`);
    const newPerkKey = push(perksRef).key;

    const newPerkItem = {
      PERK_ID: newPerkKey,
      PERK_NAME: newPerkName,
    };

    update(ref(db, `EVENTS/EVENT/${selectedCategory}/${eventId}/PRICING/${pricingId}/PERKS/${newPerkKey}`), newPerkItem)
      .then(() => {
        setPerksByPricingId({
          ...perksByPricingId,
          [pricingId]: [...(perksByPricingId[pricingId] || []), newPerkItem]
        });
        setNewPerkName("");
        setNewPerkPricingId(null);
        toast("Perk added successfully", "#", "success");
      })
      .catch(() => toast("Error adding perk", "#", "danger"));
  };

  const handleEditPerk = (perk, pricingId) => {
    setEditingPerkPricingId(pricingId);
    setEditingPerkId(perk.PERK_ID);
    setEditPerkName(perk.PERK_NAME);
  };

  const handleSavePerk = (perkId, pricingId) => {
    if (!editPerkName.trim()) {
      toast("Please enter a perk name", "#", "warning");
      return;
    }

    const db = getDatabase();
    const perkRef = ref(db, `EVENTS/EVENT/${selectedCategory}/${eventId}/PRICING/${pricingId}/PERKS/${perkId}`);

    update(perkRef, {
      PERK_NAME: editPerkName,
    })
      .then(() => {
        setPerksByPricingId({
          ...perksByPricingId,
          [pricingId]: perksByPricingId[pricingId].map((perk) =>
            perk.PERK_ID === perkId
              ? { ...perk, PERK_NAME: editPerkName }
              : perk
          )
        });
        setEditingPerkPricingId(null);
        setEditingPerkId(null);
        setEditPerkName("");
        toast("Perk updated successfully", "#", "success");
      })
      .catch(() => toast("Error updating perk", "#", "danger"));
  };

  const handleDeletePerk = (perkId, pricingId) => {
    const db = getDatabase();
    const perkRef = ref(db, `EVENTS/EVENT/${selectedCategory}/${eventId}/PRICING/${pricingId}/PERKS/${perkId}`);

    remove(perkRef)
      .then(() => {
        setPerksByPricingId({
          ...perksByPricingId,
          [pricingId]: perksByPricingId[pricingId].filter((perk) => perk.PERK_ID !== perkId)
        });
        toast("Perk deleted successfully", "#", "success");
      })
      .catch(() => toast("Error deleting perk", "#", "danger"));
  };


  // Schedule
  const handleCreateScheduleTitle = () => {
    if (!newScheduleTitle.trim()) return;

    const db = getDatabase();
    const scheduleRef = ref(db, `EVENTS/EVENT/${selectedCategory}/${eventId}/SCHEDULE`);
    const newScheduleKey = push(scheduleRef).key;

    const scheduleData = {
      SCH_NAME: newScheduleTitle,
    };

    const now = new Date();
    const datetimenow = `${String(now.getDate()).padStart(2, "0")}/${String(now.getMonth() + 1).padStart(2, "0")}/${now.getFullYear()} : ${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
    eventSCHEDULEUpdateAudit(selectedCategory, "ADD SCHEDULE TITLE", "", currentUserId, datetimenow, eventId, "Added Schedule Title: " + newScheduleTitle);

    update(ref(db, `EVENTS/EVENT/${selectedCategory}/${eventId}/SCHEDULE/${newScheduleKey}`), scheduleData)
      .then(() => {
        setSchedulesTitles([...schedulesTitles, { id: newScheduleKey, name: newScheduleTitle }]);
        setSelectedScheduleTitle(newScheduleKey);
        setNewScheduleTitle("");
        toast(`Schedule "${newScheduleTitle}" created`, "#", "success");
      })
      .catch(() => toast("Error creating schedule", "#", "danger"));
  };

  const handleAddSchedule = () => {
    if (!selectedScheduleTitle.trim()) {
      toast("Please create a schedule title first", "#", "warning");
      return;
    }
    if (!newScheduleType.trim()) return;

    const db = getDatabase();
    const itemsRef = ref(db, `EVENTS/EVENT/${selectedCategory}/${eventId}/SCHEDULE/${selectedScheduleTitle}/items`);
    const newItemKey = push(itemsRef).key;

    const startTime = `${newScheduleStartHour}:${newScheduleStartMinute}`;
    let formattedTime = startTime;
    
    if (newScheduleHasEndTime) {
      const endTime = `${newScheduleEndHour}:${newScheduleEndMinute}`;
      formattedTime = `${startTime} - ${endTime}`;
    }

    const newScheduleItem = {
      SCH_NAME: newScheduleType,
      SCH_TIME: formattedTime,
      SCH_LOCATION: newScheduleLocation,
    };

    const now = new Date();
    const datetimenow = `${String(now.getDate()).padStart(2, "0")}/${String(now.getMonth() + 1).padStart(2, "0")}/${now.getFullYear()} : ${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
    eventSCHEDULEUpdateAudit(selectedCategory, "ADD SCHEDULE ITEM", "", currentUserId, datetimenow, eventId, "Added Item: " + newItemKey);

    update(ref(db, `EVENTS/EVENT/${selectedCategory}/${eventId}/SCHEDULE/${selectedScheduleTitle}/items/${newItemKey}`), newScheduleItem)
      .then(() => {
        setScheduleList((prev) => [...prev, { ...newScheduleItem, id: newItemKey, parentScheduleId: selectedScheduleTitle }]);
        setNewScheduleType("");
        setNewScheduleStartHour("00");
        setNewScheduleStartMinute("00");
        setNewScheduleHasEndTime(false);
        setNewScheduleEndHour("00");
        setNewScheduleEndMinute("00");
        setNewScheduleLocation("");
        toast("Item added to schedule", "#", "success");
      })
      .catch(() => toast("Error adding item", "#", "danger"));
  };
 
    function eventSCHEDULEUpdateAudit(category, action, eventName, userId, timestamp, eventId, description) {
      const database = getDatabase();
      const timestampMs = new Date().getTime();
      const auditRef = ref(database, "AUDIT/" + category + "/" + eventId + "/" + timestampMs);
      const auditData = {
        action,
        eventName,
        userId,
        timestamp,
        description
      };

      set(auditRef, auditData);
    };

  const handleDeleteSchedule = (itemId, parentScheduleId) => {
    const db = getDatabase();
    const itemRef = ref(db, `EVENTS/EVENT/${selectedCategory}/${eventId}/SCHEDULE/${parentScheduleId}/items/${itemId}`);

    const now = new Date();
    const datetimenow = `${String(now.getDate()).padStart(2, "0")}/${String(now.getMonth() + 1).padStart(2, "0")}/${now.getFullYear()} : ${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
    eventSCHEDULEDeleteAudit(selectedCategory, "DELETE SCHEDULE ITEM", "", currentUserId, datetimenow, eventId, "Deleted Item: " + itemId); 

    remove(itemRef)
      .then(() => {
        setScheduleList((prev) => prev.filter((item) => item.id !== itemId));
        toast("Item deleted successfully", "#", "success");
      })
      .catch(() => toast("Error deleting item", "#", "danger"));
  };

  const handleDeleteScheduleTab = (scheduleKey) => {
    const db = getDatabase();
    const scheduleRef = ref(db, `EVENTS/EVENT/${selectedCategory}/${eventId}/SCHEDULE/${scheduleKey}`);

    const now = new Date();
    const datetimenow = `${String(now.getDate()).padStart(2, "0")}/${String(now.getMonth() + 1).padStart(2, "0")}/${now.getFullYear()} : ${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
    eventSCHEDULEDeleteAudit(selectedCategory, "DELETE SCHEDULE AND ALL ITEMS", "", currentUserId, datetimenow, eventId, "Deleted Schedule: " + scheduleKey);

    remove(scheduleRef)
      .then(() => {
        // Remove all items that belonged to this schedule from the list
        setScheduleList((prev) => prev.filter((item) => item.parentScheduleId !== scheduleKey));
        // Remove the schedule title
        setSchedulesTitles((prev) => prev.filter((s) => s.id !== scheduleKey));
        // Clear selection if deleted schedule was selected
        if (selectedScheduleTitle === scheduleKey) {
          setSelectedScheduleTitle("");
        }
        toast("Schedule and all items deleted successfully", "#", "success");
      })
      .catch(() => toast("Error deleting schedule", "#", "danger"));
  };

    function eventSCHEDULEDeleteAudit(category, action, eventName, userId, timestamp, eventId, description) {
      const database = getDatabase();
      const timestampMs = new Date().getTime();
      const auditRef = ref(database, "AUDIT/" + category + "/" + eventId + "/" + timestampMs);
      const auditData = {
        action,
        eventName,
        userId,
        timestamp,
        description
      };

      set(auditRef, auditData);
    };

  const handleEditSchedule = (schedule) => {
    setEditingScheduleId(schedule.id);
    setEditScheduleType(schedule.SCH_NAME);
    setEditScheduleLocation(schedule.SCH_LOCATION);

    // Parse the time format
    const timeStr = schedule.SCH_TIME;
    if (timeStr.includes(" - ")) {
      // Format: "HH:MM - HH:MM"
      const [startPart, endPart] = timeStr.split(" - ");
      const [startHour, startMinute] = startPart.split(":");
      const [endHour, endMinute] = endPart.split(":");
      
      setEditScheduleStartHour(startHour || "00");
      setEditScheduleStartMinute(startMinute || "00");
      setEditScheduleEndHour(endHour || "00");
      setEditScheduleEndMinute(endMinute || "00");
      setEditScheduleHasEndTime(true);
    } else {
      // Format: "HH:MM"
      const [hour, minute] = timeStr.split(":");
      setEditScheduleStartHour(hour || "00");
      setEditScheduleStartMinute(minute || "00");
      setEditScheduleHasEndTime(false);
      setEditScheduleEndHour("00");
      setEditScheduleEndMinute("00");
    }
  };

  const handleSaveSchedule = (itemId, parentScheduleId) => {
    if (!editScheduleType.trim()) {
      toast("Type is required", "#", "warning");
      return;
    }

    const db = getDatabase();
    const itemRef = ref(db, `EVENTS/EVENT/${selectedCategory}/${eventId}/SCHEDULE/${parentScheduleId}/items/${itemId}`);

    const startTime = `${editScheduleStartHour}:${editScheduleStartMinute}`;
    let formattedTime = startTime;
    
    if (editScheduleHasEndTime) {
      const endTime = `${editScheduleEndHour}:${editScheduleEndMinute}`;
      formattedTime = `${startTime} - ${endTime}`;
    }

    const now = new Date();
    const datetimenow = `${String(now.getDate()).padStart(2, "0")}/${String(now.getMonth() + 1).padStart(2, "0")}/${now.getFullYear()} : ${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
    eventSCHEDULEEditAudit(selectedCategory, "SCHEDULE ITEM EDITED", "", currentUserId, datetimenow, eventId, "Edited Item: " + itemId);
    
    update(itemRef, {
      SCH_NAME: editScheduleType,
      SCH_TIME: formattedTime,
      SCH_LOCATION: editScheduleLocation
    })
      .then(() => {
        setScheduleList((prev) =>
          prev.map((item) =>
            item.id === itemId
              ? { ...item, SCH_NAME: editScheduleType, SCH_TIME: formattedTime, SCH_LOCATION: editScheduleLocation }
              : item
          )
        );
        setEditingScheduleId(null);
        toast("Item updated successfully", "#", "success");
      })
      .catch(() => toast("Error updating schedule", "#", "danger"));
  };

    function eventSCHEDULEEditAudit(category, action, eventName, userId, timestamp, eventId, description) {
      const database = getDatabase();
      const timestampMs = new Date().getTime();
      const auditRef = ref(database, "AUDIT/" + category + "/" + eventId + "/" + timestampMs);
      const auditData = {
        action,
        eventName,
        userId,
        timestamp,
        description
      };

      set(auditRef, auditData);
    };

  // Format date helper
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const [day, month, year] = dateStr.split("/").map(Number);
    const dateObj = new Date(year, month - 1, day);
    const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    const months = ["January","February","March","April","May","June","July","August","September","October","November","December"];
    const getOrdinal = (n) => { const s = ["th","st","nd","rd"]; const v = n % 100; return s[(v - 20) % 10] || s[v] || s[0]; };
    const dayName = days[dateObj.getDay()];
    const monthName = months[month - 1];
    const dayWithSuffix = `${day}${getOrdinal(day)}`;
    return `${dayName} ${dayWithSuffix} ${monthName} ${year}`;
  };

  const getScheduleStatus = (eventDate, scheduleTime, nextScheduleTime, eventEndTime) => {
    if (!eventDate || !scheduleTime) return "future";

    // Parse event date (DD/MM/YYYY)
    const [day, month, year] = eventDate.split("/").map(Number);
    
    // Extract start time from schedule (format: "HH:MM" or "HH:MM - HH:MM")
    const startTimeStr = scheduleTime.split(" - ")[0];
    const [hours, minutes] = startTimeStr.split(":").map(Number);

    // Create datetime for this schedule item
    const scheduleDateTime = new Date(year, month - 1, day, hours, minutes, 0);
    const now = new Date();

    // Determine end time for this item (next schedule or event end time)
    let endDateTime;
    if (nextScheduleTime) {
      // Parse next schedule item's start time
      const nextStartTimeStr = nextScheduleTime.split(" - ")[0];
      const [nextHours, nextMinutes] = nextStartTimeStr.split(":").map(Number);
      endDateTime = new Date(year, month - 1, day, nextHours, nextMinutes, 0);
    } else if (eventEndTime) {
      // Use event end time
      const [endHours, endMinutes] = eventEndTime.split(":").map(Number);
      endDateTime = new Date(year, month - 1, day, endHours, endMinutes, 0);
    } else {
      // Default: 1 hour after start
      endDateTime = new Date(scheduleDateTime.getTime() + 60 * 60 * 1000);
    }

    // Compare times
    if (now < scheduleDateTime) {
      return "future";
    } else if (now >= scheduleDateTime && now < endDateTime) {
      return "active";
    } else {
      return "past";
    }
  };

  const getSortedSchedule = () => {
    if (!event.SCHEDULE) return [];

    const allItems = [];
    
    // Flatten nested schedule structure (schedule title -> items)
    Object.entries(event.SCHEDULE).forEach(([scheduleKey, scheduleData]) => {
      if (scheduleData.items && typeof scheduleData.items === 'object') {
        Object.entries(scheduleData.items).forEach(([itemKey, itemData]) => {
          if (itemData && itemData.SCH_TIME) {
            allItems.push({
              id: itemKey,
              parentScheduleId: scheduleKey,
              ...itemData
            });
          }
        });
      }
    });

    // Sort by time
    return allItems.sort((a, b) => {
      if (!a.SCH_TIME || !b.SCH_TIME) return 0;
      
      // Extract start times for sorting
      const timeA = a.SCH_TIME.split(" - ")[0];
      const timeB = b.SCH_TIME.split(" - ")[0];
      const [hourA, minA] = timeA.split(":").map(Number);
      const [hourB, minB] = timeB.split(":").map(Number);
      
      const timeAMinutes = hourA * 60 + minA;
      const timeBMinutes = hourB * 60 + minB;
      
      return timeAMinutes - timeBMinutes;
    });
  };

        
 const handleDeleteEvent = (eventId) => {
    // Delete the event from Firebase
    const database = getDatabase();
    const eventRef = ref(database, `EVENTS/EVENT/${selectedCategory}/${eventId}`);

        const now = new Date();
        const datetimenow = `${String(now.getDate()).padStart(2, "0")}/${String(now.getMonth() + 1).padStart(2, "0")}/${now.getFullYear()} : ${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
        eventDeleteUpdate(selectedCategory, "Deleted", "", currentUserId, datetimenow, eventId, "Event Deleted"); 

    import('firebase/database').then(({ remove }) => {
      remove(eventRef)
        .then(() => {
          //  console.log(`Event with ID ${eventId} deleted successfully.`);

          //  console.log(`Event with ID ${selectedCategory} deleted successfully.`);
          //  Optionally show a user-facing message or close modal

          toast("Event Delete Successfully!", "#", "success");
            
          navigate(`/${selectedCategory}`, { replace: true });
          return;


    })
        .catch((error) => {
          console.error('Error deleting event:', "#", error);
        });
    });
  };

    function eventDeleteUpdate(category, action, eventName, userId, timestamp, eventId, description) {
      const database = getDatabase();
      const timestampMs = new Date().getTime();
      const auditRef = ref(database, "AUDIT/" + category + "/" + eventId + "/" + timestampMs);
      const auditData = {
        action,
        eventName,
        userId,
        timestamp,
        description
      };

      set(auditRef, auditData);
    };

  const handleVisitSiteClick = (url) => {
    if (!url) return;

    const db = getDatabase();
    const eventRef = ref(db, `EVENTS/EVENT/${selectedCategory}/${eventId}`);

    if (currentUserId) {
      // User is logged in
      const newClickCount = (event.EVENT_SITE_CLICKS_LOGGED_IN || 0) + 1;
      update(eventRef, {
        EVENT_SITE_CLICKS_LOGGED_IN: newClickCount,
      })
        .then(() => {
          // window.open(url, '_blank');
        })
        .catch(() => {
          // toast("Error tracking click", "#", "danger");
          // window.open(url, '_blank');
        });
    } else {
      // User is not logged in
      const newClickCount = (event.EVENT_SITE_CLICKS_NOT_LOGGED_IN || 0) + 1;
      update(eventRef, {
        EVENT_SITE_CLICKS_NOT_LOGGED_IN: newClickCount,
      })
        .then(() => {
          // window.open(url, '_blank');
        })
        .catch(() => {
          // toast("Error tracking click", "#", "danger");
          // window.open(url, '_blank');
        });
    }
  };

  const handleVisitTicketSiteClick = (url) => {
    if (!url) return;

    const db = getDatabase();
    const eventRef = ref(db, `EVENTS/EVENT/${selectedCategory}/${eventId}`);

    if (currentUserId) {
      // User is logged in
      const newClickCount = (event.EVENT_TICKET_SITE_CLICKS_LOGGED_IN || 0) + 1;
      update(eventRef, {
        EVENT_TICKET_SITE_CLICKS_LOGGED_IN: newClickCount,
      })
        .then(() => {
          // window.open(url, '_blank');
        })
        .catch(() => {
          // toast("Error tracking click", "#", "danger");
          // window.open(url, '_blank');
        });
    } else {
      // User is not logged in
      const newClickCount = (event.EVENT_TICKET_SITE_CLICKS_NOT_LOGGED_IN || 0) + 1;
      update(eventRef, {
        EVENT_TICKET_SITE_CLICKS_NOT_LOGGED_IN: newClickCount,
      })
        .then(() => {
          // window.open(url, '_blank');
        })
        .catch(() => {
          // toast("Error tracking click", "#", "danger");
          // window.open(url, '_blank');
        });
    }
  };


  function getRandomInt(max) {
    const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    return letters[Math.floor(Math.random() * max)];
  }

function getRandomNum(max) {
  return Math.floor(Math.random() * max);
}

  const avarcount = Number(event.ATTENDING_COUNT);
  const avatarsToShow = Math.min(avarcount, 3);

  // Handle adding event to a group
  const handleAddEventToGroup = async (group) => {
    if (!event || !currentUser || !selectedCategory) return;

    setAddingEventToGroup(true);
    try {
      const db = getDatabase();

      // Store only a reference in GROUPS/{groupId}/EVENTS/{CATEGORY}/{eventId}
      // The full event data is in EVENTS/EVENT/{CATEGORY}/{eventId}
      await set(ref(db, `GROUPS/${group.groupId}/EVENTS/${selectedCategory}/${eventId}`), true);

      // Fetch the full group data to get GROUP_PUBLIC
      let groupPublic = "Y"; // Default to public
      let groupMemberCount = null;

      try {
        const groupRef = ref(db, `GROUPS/${group.groupId}`);
        const groupSnap = await get(groupRef);
        if (groupSnap.exists()) {
          const groupData = groupSnap.val();
          groupPublic = groupData.GROUP_PUBLIC || "Y";

          // Get group member count if public
          if (groupPublic === "Y") {
            const membersRef = ref(db, `GROUPS/${group.groupId}/MEMBERS`);
            const membersSnap = await get(membersRef);
            if (membersSnap.exists()) {
              groupMemberCount = Object.keys(membersSnap.val()).length;
            } else {
              groupMemberCount = 0;
            }
          }
        }
      } catch (error) {
        console.error("Error fetching group details:", error);
      }

      // Create tracking entry for this group: capture groupId, privacy status, and member count
      const trackingEntry = {
        groupId: group.groupId,
        GROUP_NAME: group.GROUP_NAME,
        GROUP_PUBLIC: groupPublic,
        GROUP_MEMBER_COUNT_AT_JOIN: groupMemberCount, // Only captured if public
        TIMESTAMP: new Date().toISOString(),
      };

      // Store in groupsAttendingTracking using groupId as key
      const updatedTracking = {
        ...groupsAttendingTracking,
        [group.groupId]: trackingEntry,
      };
      setGroupsAttendingTracking(updatedTracking);

      // Update local state for event associations
      setEventGroupAssociations({
        ...eventGroupAssociations,
        [group.groupId]: {
          GROUP_ID: group.groupId,
          GROUP_NAME: group.GROUP_NAME,
          GROUP_SLUG: group.GROUP_SLUG,
        }
      });

      // Save tracking data to Firebase under the event
      await set(ref(db, `EVENTS/EVENT/${selectedCategory}/${eventId}/GROUPS_ATTENDING/${group.groupId}`), trackingEntry);

      setShowGroupModal(false);
      toast(`Event added to ${group.GROUP_NAME}!`, "#", "success");
    } catch (error) {
      console.error("Error adding event to group:", error);
      toast("Error adding event to group. Please try again.", "#", "danger");
    } finally {
      setAddingEventToGroup(false);
    }
  };

  const handleRemoveEventFromGroup = async (groupId, groupName) => {
    if (!event || !eventId || !selectedCategory) return;

    try {
      const db = getDatabase();

      // Remove from group's events
      await set(ref(db, `GROUPS/${groupId}/EVENTS/${selectedCategory}/${eventId}`), null);

      // Remove from tracking
      const updatedTracking = { ...groupsAttendingTracking };
      delete updatedTracking[groupId];
      setGroupsAttendingTracking(updatedTracking);

      // Update local state
      const updated = { ...eventGroupAssociations };
      delete updated[groupId];
      setEventGroupAssociations(updated);

      // Remove tracking data from Firebase
      await set(ref(db, `EVENTS/EVENT/${selectedCategory}/${eventId}/GROUPS_ATTENDING/${groupId}`), null);

      toast(`Event removed from ${groupName}!`, "#", "success");
    } catch (error) {
      console.error("Error removing event from group:", error);
      toast("Error removing event from group. Please try again.", "#", "danger");
    }
  };

  return (
    <div>
      
      <Navbar />

      <div className="container py-4">
        <div className="row mb-4 align-items-center">
          <div className="col-6 text-start">
            <Link to={`/${backButton}`}>
              <button className="text back-button">
                <i className="bi bi-arrow-left me-1 small"></i> Back
              </button>
            </Link>
          </div>

          <div className="col-6 text-end">
            <div className="row align-end justify-content-end gap-0">
                {event.EVENT_SITE?.trim() && (
                  <div className="col-auto desktop-view-button text-end">
                    <button
                    className="btn-tertiary-outline" 
                    data-bs-toggle="modal" 
                    data-bs-target="#leavingSiteModal"
                    >
                     {event.EVENT_SOLDOUT === "Y" ? (
                      <span className="">Sold Out</span>
                     ) : (
                      <span className=""> Visit Site </span>
                     )}
                    </button>
                  </div>
                )}

              <div className="col-auto desktop-view-button text-end">
                <button  target="_blank" rel="noopener noreferrer" className="btn-tertiary-outline" onClick={handleCopyUrl}>
                  <i className="bi bi-reply me-1 flip-transform"></i> Share </button>
              </div>

              {currentUserId && currentUserRole && (event.EVENT_OWNER === currentUserId || currentUserRole === 'ADMIN') && (
                  <div className="col-auto text-end"> 
                    <button className="btn-tertiary-outline" onClick={toggleView}>
                      <i className="bi bi-gear-wide me-1 small"></i> {showEdit ? "Cancel" : "Edit"}
                    </button>
                  </div>
              )}
            </div>
          </div>

        </div>


        {/* MAIN CONTENT */}
        {!showEdit && (
          <> 

              {/* Check visibility permissions */}
              {(event?.EVENT_PUBLIC === "Y"   || event?.EVENT_OWNER === currentUserId || isUserWhitelisted) ? (
                <>
   <div className="row justify-content-center mb-4">
  <div className="col-md-12 image-holder rounded-lg" style={{ overflow: 'hidden' }}>

    {imageSrc ? (
      <img
        className="w-full object-cover max-sm:h-50 sm:aspect-[4/1]"
        src={imageSrc}
        alt="event-banner"
        style={{
          viewTransitionName: `event-image-${eventId}`,
          display: "block",
          width: "100%",
          borderRadius: "12px"
        }}
        loading="eager"
      />
    ) : (
      <div className={`event-image-fallback event-image-${event.EVENT_TYPE} w-full object-cover max-sm:h-50 sm:aspect-[4/1]`}></div>
    )}

  </div>
</div>


    <div className="text-start mb-2">
      <span className="event-badges-page me-2">
        <i className={`bi bi-circle-half me-1 ${event.EVENT_TYPE}-badge`}></i>
        {event.EVENT_TYPE}
      </span>

      {event.EVENT_VERIFIED === "Y" && (
        <span className="event-badges-page me-2">
          <i className="bi bi-check2-circle me-1 verified-badge"></i>
          Verified
        </span>
      )}

      {event.EVENT_SPONSORED === "Y" && (
        <span className="event-badges-page me-2">
          <i className="bi bi-check2-circle me-1 sponsored-badge"></i>
          Sponsored
        </span>
      )}

      {event.EVENT_SHOWREACTIONS === "Y" && likeCount > 0 && (
        <span className="event-badges-page me-2">
          <i className="bi bi-hand-thumbs-up me-2 recommend-badge2"></i>
          {likeCount} Recommend
        </span>
      )}
      
      
      {event.EVENT_WERECOMMEND === "Y" &&(
        <span className="event-badges-page me-2">
          <i className="bi  bi-suit-heart me-2 we-recommend-badge2"></i>
            We Recommend
          </span>
      )}
    </div>


                <div className="row justify-content-between desktop-sticky event-sub-navbar blap">
                    <div className="col-12 text-start">

                      <h1 className="event-heading-start mb-1">{event.EVENT_PUBLIC === "N" ? <span className="small"><i className="bi bi-lock"></i> </span> : ""} {event.EVENT_NAME}</h1>

                      <div className="row justify-content-between align-items-center align-middle mb-3">

                      <div className="col-auto  text-start "><h5 className="event-sub-title mb-0">
                        <span className="eventownername me-1">{eventOwner?.USERDISPLAYNAME}</span>
                          {eventOwner?.USERISADMIN === "Y" && (<i className="bi bi-shield-check me-1 admin-badge"></i>)} 
                          {(eventOwner?.USERISVERYFIED === "Y") && (<i className="bi bi-check2-circle me-1 verified-badge"></i>)}
                        </h5>
                      </div>

                        <div className="col-auto text-end d-flex gap-2">
                          {currentUserId && (
                            <> 
                              <button
                                className={`mb-0 text-center ${joinedEvents[eventId] ? 'btn-leave-round' : 'btn-join-round'}`}
                                onClick={() =>
                                  toggleJoinRace({
                                    eventId: eventId,
                                    joinedAt: Date.now(),
                                  })
                                }
                              >
                        
                                {joinedEvents[eventId] ? (
                                  <>
                                    <i className="bi bi-arrow-left me-1 small"></i> Leave 
                                  </>
                                ) : (
                                  <>
                                    Attend <i className="bi bi-arrow-right ms-1 small"></i>
                                  </>
                                )}
                              </button>

                              {userJoinedGroups.length > 0 && (
                                <button
                                  className="mb-0 text-center btn-join-round"
                                  onClick={() => setShowGroupModal(true)}
                                  title="Add event to your group"
                                >
                                  <i className="bi bi-people me-1 small"></i> Join with
                                </button>
                              )}
                        </>
                          )}

                          {/* <div className="col-auto desktop-view-button"> <i className="bi bi-dash"></i> </div> */}
                          


                          </div>

                      </div>


                  </div>
                </div>

          <div className="row justify-content-between mb-5">

            <div className="col-md-8 col-sm-12">

              
                <div className="row justify-content-between mt-4">


                  {event.TOK && Object.keys(event.TOK).length > 0 && (
                    <> 
                        {Object.values(event.TOK).map((item, idx) => (
                          <div className="col-md-12 col-sm-12 text-start mb-2 mobile-view-button" >
                          <div key={idx} className={`ttk-banner-section-${item.TYPE} mb-3`}>
                              <div className="container">
                                <h5 className="ttk-header mb-2"> {item.HEADER}</h5> 
                                <p className="ttk-text mb-0"> {item.PARA} </p> 
                              </div>
                          </div>
                          </div>
                        ))}
                    </>
                  )}


                  <div className="col-md-12 col-sm-12 text-start mb-5" id="description">
                    <h5 className="event-sub-title">About {event.EVENT_NAME}</h5>
                    <div className="event-text">{ledescription}</div>
                  </div>

                  
                  <div className="col-md-12 col-sm-12 text-start mb-5" id="dateTime">
                    <h5 className="event-sub-title">Date/Time</h5>
                    {event.EVENT_EXTRADAYS > 0 && ( 
                      <> 
                        {/* <p className="event-text">
                          Multi-Day Event - <span className="extra-days">{parseInt(event.EVENT_EXTRADAYS) + 1} Days</span> <br></br>
                        </p> */}
                        <p className="event-text mb-0 text-white ed">
                          Starting Day:
                        </p>
                       </>
                    )}

                    <p className="event-text event-date-time">
                      <span>{formatDate(event.EVENT_DATE)}</span> - <i className="bi bi-alarm me-1"></i> <span>{event.EVENT_STARTTIME}</span> - <span>{event.EVENT_ENDTIME}</span>
                    </p> 

                    {event.EVENT_EXTRADAYS > 0 && ( 
                      <p className="event-text mb-0">
                        <span className="text-white ed">Ending Day:</span> <br></br>
                        <span className="event-date-time">{getEventFullEndingDate(event.EVENT_DATE, event.EVENT_EXTRADAYS)}</span>
                      </p>
                    )}
            
                  </div>


                  <div className="col-md-12 col-sm-12 text-start mb-5" id="pricing">
                    <h5 className="event-sub-title">Pricing Options</h5>
                    <div className="pricing-options-menu">

                      {(event.EVENT_PRICEMIN || event.EVENT_PRICE) && (
                        <div className="pricing-column">
                          <div className="pricing-header">General Tickets</div>

                          <div className="pricing-includes">
                            <p className="pricing-includes-label">Tickets includes</p>
                            <div className="pricing-features">
                              {event.EVENT_NAME && (
                                <div className="pricing-feature-item">
                                  <i className="bi bi-check-circle"></i>
                                  <span>Entry to {event.EVENT_NAME}</span>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="pricing-price-section">
                            {event.EVENT_PRICEMIN && event.EVENT_PRICE && event.EVENT_PRICEMIN !== event.EVENT_PRICE && (
                              <div className="pricing-price-range mb-0 pb-0">
                                Starting From 
                              </div>
                            )}
                            <div className="pricing-price-main">
                              {event.EVENT_PRICE > 0 ? (
                                <>
                                   {event.EVENT_PRICEMIN && event.EVENT_PRICE && event.EVENT_PRICEMIN !== event.EVENT_PRICE && ( 
                                      <> 
                                      <span className="pricing-price-amount"> £{event.EVENT_PRICEMIN}  </span> - {"  "}
                                      </>
                                    )}
                                  <span className="pricing-price-amount mb-0 pb-0">   
                                    £{event.EVENT_PRICE}</span>
                                  <div className="pricing-price-period">per ticket</div>
                                </>
                              ) : (
                                <>
                                  <span className="pricing-price-amount">FREE</span>
                                </>
                              )}
                            </div>
                          </div>

                          {(event.EVENT_TICKETSITE?.trim() || event.EVENT_SITE?.trim()) && (
                          <button
                            className="pricing-cta-button" 
                            data-bs-toggle="modal" 
                            data-bs-target="#leavingSiteTicketsModal"
                            >

                          {event.EVENT_SOLDOUT === "Y" ? (
                            <span className="">
                              View Tickets - Sold Out
                            </span>
                          ) : "Get Tickets"}

                              <i className="bi bi-arrow-right ms-2"></i>
                          </button>
                          )}
                        </div>
                      )}

                      {/* Custom pricing tiers now rendered from pricingList */}
                      {pricingList && pricingList.length > 0 && pricingList.map((pricing) => (
                        <div key={pricing.PRICING_ID} className={`pricing-column ${pricing.PRICING_BESTSELLER === "Y" ? "best-value" : ""}`}>
                          <div className={`pricing-header align-items-center justify-content-between ${pricing.PRICING_BESTSELLER === "Y" ? "best-value" : ""}`}>{pricing.PRICING_NAME} {pricing.PRICING_BESTSELLER === "Y" && <span className="best-value-badge">Most Popular</span>}</div>

                          <div className={`pricing-includes ${pricing.PRICING_BESTSELLER === "Y" ? "best-value" : ""}`}>
                            <p className={`pricing-includes-label ${pricing.PRICING_BESTSELLER === "Y" ? "best-value" : ""}`}>Tickets includes</p>
                            <div className={`pricing-features ${pricing.PRICING_BESTSELLER === "Y" ? "best-value" : ""}`}>
                                {
                                  perksByPricingId[pricing.PRICING_ID] &&
                                  perksByPricingId[pricing.PRICING_ID].length > 0 ? (
                                    perksByPricingId[pricing.PRICING_ID].map((perk) => (
                                      <div key={perk.PERK_ID} className={`pricing-feature-item ${pricing.PRICING_BESTSELLER === "Y" ? "best-value" : ""}`}>
                                        <i className="bi bi-check-circle "></i>
                                        <span>{perk.PERK_NAME}</span>
                                      </div>
                                    ))
                                  ) : (
                                    <div className={`pricing-feature-item ${pricing.PRICING_BESTSELLER === "Y" ? "best-value" : ""}`}>
                                      <i className="bi bi-x-circle no-text"></i>
                                      <span>No perks added</span>
                                    </div>
                                  )
                                }
                            </div>
                          </div>

                          <div className="pricing-price-section">
                            {pricing.PRICING_PRICEMIN && pricing.PRICING_PRICEMAX && pricing.PRICING_PRICEMIN !== pricing.PRICING_PRICEMAX && (
                              <div className="pricing-price-range mb-0 pb-0">
                                Starting From
                              </div>
                            )}
                            <div className="pricing-price-main">
                              {pricing.PRICING_PRICEMIN && pricing.PRICING_PRICEMAX && pricing.PRICING_PRICEMIN !== pricing.PRICING_PRICEMAX && (
                                <>
                                  <span className="pricing-price-amount">£{pricing.PRICING_PRICEMIN}</span>
                                  <span className="pricing-price-range-separator"> - </span>
                                </>
                              )}
                              <span className="pricing-price-amount">£{pricing.PRICING_PRICEMAX || pricing.PRICING_PRICEMIN}</span>
                              <div className="pricing-price-period">per ticket</div>
                            </div>
                          </div>

                          {(event.EVENT_TICKETSITE?.trim() || event.EVENT_SITE?.trim()) && (
                           <button
                            className="pricing-cta-button" 
                            data-bs-toggle="modal" 
                            data-bs-target="#leavingSiteTicketsModal"
                            >
                              
                            {event.EVENT_SOLDOUT === "Y" ? (
                              <span className="">
                                View Tickets - Sold Out
                              </span>
                            ) : "Get Tickets"}

                              <i className="bi bi-arrow-right ms-2"></i>
                            </button>
                          )}
                        </div>
                      ))}

                    </div>

                  
                  </div>
                  
                  <div className="col-md-6 col-sm-12 text-start mb-0" id="location">
                    <h5 className="event-sub-title">Getting to {event.EVENT_NAME}</h5>
                  </div>

                  {getGoogleMapsEmbed(event.EVENT_LOCATION, event.EVENT_POSTCODE) && (
                    <div className="col-md-12 text-start mb-2">
                      <iframe
                        className="rounded-lg"
                        src={getGoogleMapsEmbed(event.EVENT_LOCATION, event.EVENT_POSTCODE)}
                        width="100%"
                        height="250"
                        allowFullScreen
                        loading="lazy"
                        title={`Map for ${event.EVENT_NAME}`}
                      />
                    </div>
                  )}

                  <p className="user-select-all  mb-5">
                    <i className="bi bi-geo-alt"></i> {event.EVENT_LOCATION}, {event.EVENT_POSTCODE}
                  </p>

                  

                    {/* Schedule Section */}
                    {event.SCHEDULE && Object.keys(event.SCHEDULE).length > 0 && (
                      <div className="col-md-12 col-sm-12  mb-5" id="schedule">
                        <h5 className="event-sub-title"> Schedule</h5>

                          {/* Schedule Tabs Navigation */}
                          {(() => {
                            const scheduleKeys = Object.keys(event.SCHEDULE);
                            const firstScheduleKey = selectedPublicScheduleTab && scheduleKeys.includes(selectedPublicScheduleTab) ? selectedPublicScheduleTab : scheduleKeys[0];
                            
                            // Initialize selectedPublicScheduleTab if not set
                            if (!selectedPublicScheduleTab && firstScheduleKey) {
                              setSelectedPublicScheduleTab(firstScheduleKey);
                            }

                            return (
                              <>
                                {scheduleKeys.length > 1 && (
                                  <ul className="nav nav-tabs mb-3" role="tablist">
                                    {scheduleKeys.map((scheduleKey) => (
                                      <li key={scheduleKey} className="nav-item" role="presentation">
                                        <button
                                          className={`nav-link ${(selectedPublicScheduleTab || firstScheduleKey) === scheduleKey ? 'active' : ''}`}
                                          type="button"
                                          role="tab"
                                          onClick={() => setSelectedPublicScheduleTab(scheduleKey)}
                                        >
                                          {event.SCHEDULE[scheduleKey].SCH_NAME || `Schedule ${scheduleKey.slice(0, 5)}`}
                                        </button>
                                      </li>
                                    ))}
                                  </ul>
                                )}

                                <div className="schedule-section">
                                  <div className="row schedule-header dbv2">
                                    <div className="col-2"><strong> Time </strong> </div>
                                    <div className="col-4"> <strong> Type </strong> </div> 
                                    <div className="col-auto"> <strong> Where </strong> </div> 
                                  </div>

                                  {getSortedSchedule()
                                    .filter(item => item.parentScheduleId === (selectedPublicScheduleTab || firstScheduleKey))
                                    .map((item, idx) => {
                                      const filteredSchedule = getSortedSchedule().filter(i => i.parentScheduleId === (selectedPublicScheduleTab || firstScheduleKey));
                                      const nextItem = idx < filteredSchedule.length - 1 ? filteredSchedule[idx + 1] : null;
                                      const status = getScheduleStatus(
                                        event.EVENT_DATE,
                                        item.SCH_TIME,
                                        nextItem ? nextItem.SCH_TIME : null,
                                        idx === filteredSchedule.length - 1 ? event.EVENT_ENDTIME : null 
                                      );
                                      return (
                                        <div key={item.id} className={`schedule-item-${status}`}>
                                          <div className={`row align-items-center schedule-row-${status} py-2 mb-1`}>
                                            <div className="col-md-2 col-sm-auto">{status === "future" ? <i className="bi bi-arrow-clockwise me-1 future-badge"></i> : status === "past" ? <i className="bi bi-check2-circle me-1  verified-badge"></i> : <i className="bi bi-play-fill me-1 past-badge"></i>} {item.SCH_TIME} </div>
                                            <div className="col-md-4 col-sm-auto">  <i className="bi bi-triangle-half me-1 mobile-view-button"></i> {item.SCH_NAME} </div> 
                                            <div className="col-md-auto col-sm-auto"> <i className="bi bi-geo-alt me-1 mobile-view-button"></i>{item.SCH_LOCATION ? item.SCH_LOCATION : "..."} </div> 
                                          </div>
                                        </div>
                                      );
                                    })}
                                </div>
                              </>
                            );
                          })()}
                    
                      </div>
                    )}

            {(
              (event.EVENT_PHOTOGRAPHYALLOWED ?? "") !== "" ||
              (event.EVENT_DRONESALLOWED ?? "") !== "" ||
              Object.keys(event.FAQ ?? {}).length > 0
            ) && (

             <div className="col-12 text-start mb-5 mobile-view-button" id="faqs">
                         
                          <div className="accordion sidebar-accordion" id="faqAccordion sidebar-accordion">
                             <h5 className="event-sub-title ms-2 mb-0">FAQs</h5>
     
                        {/* FAQ Section PC */}
                        {event.FAQ && Object.keys(event.FAQ).length > 0 && (
                          <>
                            {Object.values(event.FAQ).map((faq, idx) => {
                              const collapseId = `faqCollapse${idx}`;
                              const headingId = `faqHeading${idx}`;

                              return (
                                <div className="accordion-item" key={idx}>
                                  <h2 className="accordion-header" id={headingId}>
                                    <button className="accordion-button accordionsidebar-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target={`#${collapseId}`} aria-expanded="false" aria-controls={collapseId}
                                    >
                                      {faq.QUESTION}
                                    </button>
                                  </h2>
                                  <div id={collapseId} className="accordion-collapse accordionsidebar-button collapse" aria-labelledby={headingId} data-bs-parent="#faqAccordion" // optional: ensures only one open at a time
                                  >
                                    <div className="accordion-body">
                                      {faq.ANSWER}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}

                         

                      </>
                        )}

                      {((event.EVENT_PHOTOGRAPHYALLOWED ?? "") !== "" || (event.EVENT_DRONESALLOWED ?? "") !== "") && (
                        <>
                          <h5 className="event-sub-title my-3 ms-2">Photography</h5>

                          {(event.EVENT_PHOTOGRAPHYALLOWED ?? "") !== "" && (
                            <div className="row justify-content-between align-items-center mb-2 event-text mx-0">
                              <div className="col-auto">Photography</div>
                              <div className="col-auto">
                                {event.EVENT_PHOTOGRAPHYALLOWED === "N" && <span><i className="bi bi-x-circle no-text"></i></span>}
                                {event.EVENT_PHOTOGRAPHYALLOWED === "Y" && <span><i className="bi bi-check-circle yes-text"></i></span>}
                                {event.EVENT_PHOTOGRAPHYALLOWED === "M" && <span><i className="bi bi-dash-circle default-text"></i> Media Team Only</span>}
                              </div>
                            </div>
                          )}

                          {(event.EVENT_DRONESALLOWED ?? "") !== "" && (
                            <div className="row justify-content-between align-items-center mb-2 event-text mx-0">
                              <div className="col-auto">Drones</div>
                              <div className="col-auto">
                                {event.EVENT_DRONESALLOWED === "N" && <span><i className="bi bi-x-circle no-text"></i></span>}
                                {event.EVENT_DRONESALLOWED === "Y" && <span><i className="bi bi-check-circle yes-text"></i></span>}
                                {event.EVENT_DRONESALLOWED === "M" && <span><i className="bi bi-dash-circle default-text"></i> Media Team Only</span>}
                              </div>
                            </div>
                          )}
                        </>
                      )}
 
                          </div>
                         </div>
                      )}


            {event.EVENT_SHOWREACTIONS === "Y" && ( 
             <div className="col-12 text-start mb-5 mobile-view-button">
              <h5 className="event-sub-title ms-2 mb-3">Recommendations</h5>

              <div className="row justify-content-between align-items-center mb-2 event-text mx-0">
               
                <div className="col-auto">
                  <button 
                    className={` btn-sm btn-react`}
                    onClick={() => handleReactionClick('LIKE')}
                    title={currentUserId ? "" : ""}
                  >
                    <i className="bi bi-hand-thumbs-up me-2 recommend-badge2"></i> 
                     {likeCount > 0 ? `${likeCount} Recommend` : "Recommend"}
                  </button>
                </div>

                <div className="col-auto hide">
                  <button 
                    className={`btn btn-sm btn-react`}
                    onClick={() => handleReactionClick('DISLIKE')}
                    title={currentUserId ? "" : ""}
                  >
                    <i className="bi bi-hand-thumbs-down me-1 dontrecommend-badge2"></i> 
                     {dislikeCount}
                  </button>
                </div>

                </div>
             </div>
             )} 

                    {(avarcount > "0") && (
                        <div className="col-12 text-start mb-5 ">
                          <h5 className="event-sub-title"> Attendees <span data-bs-toggle="collapse" href="#atthelp" role="button" aria-expanded="false" aria-controls="atthelp"><i className="bi bi-question-circle"></i></span></h5>

                            <div className="col-12 text-start">
                                <h5 className="mb-0">
                    
                                {avarcount > 0 && (
                                  <div className="avatar-group -space-x-4 mb-2">

                                    {[...Array(avatarsToShow)].map((_, i) => (
                                      <div key={i} className={`avatar avatar-placeholder ap-${getRandomNum(4)} text-white`}>
                                        <div className="w-12 text-center">
                                          <span>{getRandomInt(24)}</span>
                                        </div>
                                      </div>
                                    ))}

                                    {avarcount > avatarsToShow && (
                                      <div className="avatar avatar-placeholder ap-4 text-white">
                                        <div className="w-12 text-center">
                                          <span>+</span>
                                        </div>
                                      </div>
                                    )}

                                  </div>
                                )}

                                {(event.ATTENDING_COUNT >= 2) && (() => {
                                  const eventCount = Number(event.ATTENDING_COUNT) || 0;
                                  const groupCounts = Object.values(groupsAttendingTracking)
                                    .filter((g) => g.GROUP_PUBLIC === "Y" && g.GROUP_MEMBER_COUNT_AT_JOIN !== null)
                                    .reduce((sum, g) => sum + g.GROUP_MEMBER_COUNT_AT_JOIN, 0);
                                  const totalCount = eventCount + groupCounts;
                                  return <span className="mb-3 h4"> +{totalCount} People Attending </span>;
                                })()} 
                                </h5>

                                <div className="collapse" id="atthelp">
                                  <p className="event-text mb-0 text-small mt-2">
                                     The Attending Count is based on the number of users who have joined this event and the number of members in groups that have joined this event. 
                                     <br />
                                     This information is sourced solely from this website and does not reflect the actual amount from the event.
                                  </p>
                                </div>
                             
                               
                              
                            </div>
                          </div>
                      )}
                  




                  <div className="col-12 text-end mobile-view-button">
                    <button type="button " className="btn btn-outline-danger" data-bs-toggle="modal" data-bs-target="#ReportModal">
                      Report Event
                    </button>
                  </div>


                </div>

            </div>

          <div className="col-md-4 col-sm-12 text-start mb-4 desktop-view-button">
            {/* desktop-sticky */}
                    <div className="col-12 mt-3 mb-3">
                    {event.TOK && Object.keys(event.TOK).length > 0 && (
                      <> 
                          {Object.values(event.TOK).map((item, idx) => (
                            <div key={idx} className={`ttk-banner-section-${item.TYPE} mb-3`} >
                                <div className="container">
                                  <h5 className="ttk-header mb-2"> {item.HEADER}</h5> 
                                  <p className="ttk-text mb-0"> {item.PARA} </p> 
                                </div>
                            </div>
                          ))}
                      </>
                    )}

            {event.EVENT_SHOWREACTIONS === "Y" && ( 
             <div className="event-sidebar p-3 rounded-lg mb-3">
              <h5 className="event-sub-title ms-2 mb-3">Recommendations</h5>

              <div className="row justify-content-between align-items-center mb-2 event-text mx-0">
               
                <div className="col-auto">
                  <button 
                    className={` btn-sm btn-react`}
                    onClick={() => handleReactionClick('LIKE')}
                    title={currentUserId ? "" : ""}
                  >
                    <i className="bi bi-hand-thumbs-up me-2 recommend-badge2"></i> 
                     {likeCount > 0 ? `${likeCount} Recommend` : "Recommend"}
                  </button>
                </div>

                <div className="col-auto hide">
                  <button 
                    className={`btn btn-sm btn-react`}
                    onClick={() => handleReactionClick('DISLIKE')}
                    title={currentUserId ? "" : ""}
                  >
                    <i className="bi bi-hand-thumbs-down me-1 dontrecommend-badge2"></i> 
                     {dislikeCount}
                  </button>
                </div>

                </div>
             </div>
            )} 

            {(
              (event.EVENT_PHOTOGRAPHYALLOWED ?? "") !== "" ||
              (event.EVENT_DRONESALLOWED ?? "") !== "" ||
              Object.keys(event.FAQ ?? {}).length > 0
            ) && (

             <div className="col-12 text-start mb-3" id="faqs">
                         
                          <div className="accordion sidebar-accordion" id="faqAccordion sidebar-accordion">
                             <h5 className="event-sub-title ms-2 mb-0">FAQs</h5>
     
                        {/* FAQ Section PC */}
                        {event.FAQ && Object.keys(event.FAQ).length > 0 && (
                          <>
                            {Object.values(event.FAQ).map((faq, idx) => {
                              const collapseId = `faqCollapse${idx}`;
                              const headingId = `faqHeading${idx}`;

                              return (
                                <div className="accordion-item" key={idx}>
                                  <h2 className="accordion-header" id={headingId}>
                                    <button className="accordion-button accordionsidebar-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target={`#${collapseId}`} aria-expanded="false" aria-controls={collapseId}
                                    >
                                      {faq.QUESTION}
                                    </button>
                                  </h2>
                                  <div id={collapseId} className="accordion-collapse accordionsidebar-button collapse" aria-labelledby={headingId} data-bs-parent="#faqAccordion" // optional: ensures only one open at a time
                                  >
                                    <div className="accordion-body">
                                      {faq.ANSWER}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}

                         

                      </>
                        )}

      {((event.EVENT_PHOTOGRAPHYALLOWED ?? "") !== "" || (event.EVENT_DRONESALLOWED ?? "") !== "") && (
                        <>
                          <h5 className="event-sub-title my-3 ms-2">Photography</h5>

                          {(event.EVENT_PHOTOGRAPHYALLOWED ?? "") !== "" && (
                            <div className="row justify-content-between align-items-center mb-2 event-text mx-0">
                              <div className="col-auto">Photography</div>
                              <div className="col-auto">
                                {event.EVENT_PHOTOGRAPHYALLOWED === "N" && <span><i className="bi bi-x-circle no-text"></i></span>}
                                {event.EVENT_PHOTOGRAPHYALLOWED === "Y" && <span><i className="bi bi-check-circle yes-text"></i></span>}
                                {event.EVENT_PHOTOGRAPHYALLOWED === "M" && <span><i className="bi bi-dash-circle default-text"></i> Media Team Only</span>}
                              </div>
                            </div>
                          )}

                          {(event.EVENT_DRONESALLOWED ?? "") !== "" && (
                            <div className="row justify-content-between align-items-center mb-2 event-text mx-0">
                              <div className="col-auto">Drones</div>
                              <div className="col-auto">
                                {event.EVENT_DRONESALLOWED === "N" && <span><i className="bi bi-x-circle no-text"></i></span>}
                                {event.EVENT_DRONESALLOWED === "Y" && <span><i className="bi bi-check-circle yes-text"></i></span>}
                                {event.EVENT_DRONESALLOWED === "M" && <span><i className="bi bi-dash-circle default-text"></i> Media Team Only</span>}
                              </div>
                            </div>
                          )}
                        </>
                      )}
 
                          </div>
             </div>
          )}



                

                      <div className="col-12 text-end">
                        
                        <button className="btn btn-outline-danger" data-bs-toggle="modal" data-bs-target="#ReportModal">
                          Report Event
                        </button>
                      </div>


                    </div>


          </div>

                {/* <div className="col-12 text-start mb-3 mobile-view-button"> 
                  <div className="row">
                    {event.EVENT_SITE?.trim() && (
                      <div className="col-6">
                        <button
                        className="btn-tertiary-outline" 
                        data-bs-toggle="modal" 
                        data-bs-target="#leavingSiteModal"
                        >
                        {event.EVENT_SOLDOUT === "Y" ? (
                          <span className="">Sold Out</span>
                        ) : (
                          <span className=""> Visit Site </span>
                        )}
                        </button>
                      </div>
                    )}

                    <div className="col-6 text-end">
                       <button  target="_blank" rel="noopener noreferrer" className="btn-tertiary-outline " onClick={handleCopyUrl}>
                         <i className="bi bi-reply me-1 "></i>  Share
                       </button> 
                    </div> 
                  </div>
                </div> */}


          </div>

                  {/* EVENT HIDDEN OR REQUEST TO JOIN SECTION */}
                </> 
              ) : event?.EVENT_ATTENDBYJOINING === "Y" ? (
                <>
                  <div className="row justify-content-center mb-5">
                    <div className="col-md-8 col-sm-12 text-center">
                      <h1 className="event-heading-start mb-1">{event.EVENT_NAME}</h1>
                      <div className="row mb-2">
                        <div className="col-auto pe-0 mx-auto">
                          <button className={`event-badges`}> <i className={`bi bi-circle-half me-1 ${event.EVENT_TYPE}-badge`}></i> {event.EVENT_TYPE} </button>
                        </div>
                      </div>
                      <div className="filter-section mb-5"></div>
                      
                      <div className="mb-5 p-4 event-sidebar">
                        <h3 className="mb-3">Join Request</h3>
                        <p className="mb-4">This is a private event. To view more details and attend, you need to request to join.</p>
                        <button className="btn btn-primary" onClick={handleRequestToJoin}>
                          <i className="bi bi-person-plus me-2"></i>Request to Join
                        </button>
                      </div>

                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="row justify-content-center mb-5">
                    <div className="col-md-8 col-sm-12 text-center">

                      <div className="my-5 p-4 event-sidebar">
                        <h3 className="mb-3"><i className="bi bi-lock me-2"></i> Event is Hidden</h3>
                        <p className="">Sorry, you don't have permission to view this event.</p>
                      </div>
                      
                    </div>
                  </div>
                </>
              )}

                  {/* REPORT MODAL */}
                  <div className="modal fade" id="ReportModal" tabIndex="-1" aria-labelledby="ReportModalLabel" aria-hidden="true">
                    <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
                      <div className="modal-content">

                        
                          <div className="modal-body">
                          <div className="row justify-content-between align-items-start mb-3">
                            <div className="col-auto">
                            
                              <h3 className="modal-title mb-0" id="">Report</h3>
                              <p className="mb-0 event-text small">See something not right? report it here.</p>
                            </div>
                            <div className="col-auto">
                              <button
                                type="button"
                                className="close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                              >
                                <i className="bi bi-x-lg"></i>
                              </button>
                              </div>
                          </div>
                          </div>

                          <form onSubmit={handleReport}>
                          <div className="modal-body">

                          
                            <div className="mb-3">
                              <label className="form-label">Reason</label>
                              
                              <select
                                className="form-select"
                                value={reportReason}
                                onChange={(e) => setReportReason(e.target.value)}
                                required
                              >
                                <option value="" disabled>Select a reason</option>
                                <option value="spam">Spam</option>
                                <option value="harassment">Harassment</option>
                                <option value="inappropriate content">Inappropriate Content</option>
                                <option value="duplicated event">Duplicated Event</option>
                                <option value="fake event">Fake Event</option>
                                <option value="broken event">Broken Event</option>
                                <option value="broken url">Broken URL</option>
                                <option value="other">Other</option>
                              </select>
                            </div>

                            <div className="mb-3">
                              <label className="form-label">Description</label>
                              <textarea
                                className="form-control"
                                value={reportDescription}
                                onChange={(e) => setReportDescription(e.target.value)}
                                required
                              />
                            </div>
                          </div>

                          <div className="modal-footer border-0">
                            <button type="submit" className="btn btn-primary">
                              Submit 
                            </button>
                          </div>

                          </form>

                      </div>
                    </div>
                  </div>



           </>
        )}

        {/* EDIT SECTION */}
        {showEdit && currentUserId && currentUserRole && (event.EVENT_OWNER === currentUserId || currentUserRole === 'ADMIN') && (
          <div className="collapse show ">
            <div className="card-body">
   
                    <nav>
                      <div className="nav nav-tabs" id="nav-tab" role="tablist">
                        <button className="nav-link active" id="nav-home-tab" data-bs-toggle="tab" data-bs-target="#nav-home" type="button" role="tab" aria-controls="nav-home" aria-selected="true">General Settings</button>
                        <button className="nav-link" id="nav-profile-tab" data-bs-toggle="tab" data-bs-target="#nav-profile" type="button" role="tab" aria-controls="nav-profile" aria-selected="false">Page Alert</button>
                        <button className="nav-link" id="nav-profile-tab" data-bs-toggle="tab" data-bs-target="#nav-schedu" type="button" role="tab" aria-controls="nav-schedu" aria-selected="false">Schedule</button>
                        <button className="nav-link" id="nav-contact-tab" data-bs-toggle="tab" data-bs-target="#nav-contact" type="button" role="tab" aria-controls="nav-contact" aria-selected="false">FAQs</button>
                        <button className="nav-link" id="nav-whitelist-tab" data-bs-toggle="tab" data-bs-target="#nav-whitelist" type="button" role="tab" aria-controls="nav-whitelist" aria-selected="false">Whitelist</button>
                        <button className="nav-link" id="nav-pricing-tab" data-bs-toggle="tab" data-bs-target="#nav-pricing" type="button" role="tab" aria-controls="nav-pricing" aria-selected="false">Pricing & Perks</button>
                        <button className="nav-link" id="nav-data-tab" data-bs-toggle="tab" data-bs-target="#nav-data" type="button" role="tab" aria-controls="nav-data" aria-selected="false">Event Data</button>
                        <button className="nav-link" id="nav-delete-tab" data-bs-toggle="tab" data-bs-target="#nav-delete" type="button" role="tab" aria-controls="nav-delete" aria-selected="false">Delete Event</button>
                      </div>
                    </nav>
                    <div className="tab-content" id="nav-tabContent">

                      <div className="tab-pane fade show active" id="nav-home" role="tabpanel" aria-labelledby="nav-home-tab">
                        
                            <div className="row justify-content-between align-items-end mt-5 mb-0">
                              <div className="col-sm-12 ">
                                  <h2>General Settings </h2>
                                  <p className="card-text text-gray-500">Update the general settings for this event.</p>
                              </div>
                            </div>

                          <div className="mt-5">


                                        {currentUserRole === 'ADMIN' && (
                                          <div className="admin-only mb-5">
                                            <p className="admin-only-header mb-0">Admin Section.</p>
                                            <p className=" small form-help-text ">This section is only visable for admins.</p>
                                            <div className="row mb-3">         
                                              <div className="col-4 col-md-4 col-sm-4">
                                                <label htmlFor={`EditEventSponsored-${eventId}`} className="form-label">
                                                  Sponsored 
                                                </label>
                                                  <div className="form-check form-switch">
                                                    <input
                                                      className="form-check-input"
                                                      type="checkbox"
                                                      id={`EditEventSponsored-${eventId}`}
                                                      checked={Sponsored}
                                                      onChange={(e) => setSponsored(e.target.checked)}
                                                    />
                                                    <label
                                                      className="form-check-label"
                                                      htmlFor={`EditEventSponsored-${eventId}`}
                                                    >
                                                      {/* {Sponsored ? "Yes" : "No"} */}
                                                    </label>
                                                  </div>
                                              </div>

                                              <div className="col-4 col-md-4 col-sm-4">
                                                <label htmlFor={`EditEventVerified-${eventId}`} className="form-label">
                                                  Verified 
                                                </label>

                                               <div className="form-check form-switch width-fit">
                                                <input
                                                  className="form-check-input "
                                                  type="checkbox"
                                                  id={`EditEventVerified-${eventId}`}
                                                  checked={Verified}
                                                  onChange={(e) => setVerified(e.target.checked)}
                                                />
                                                <label
                                                  className="form-check-label"
                                                  htmlFor={`EditEventVerified-${eventId}`}
                                                >
                                                  {/* {Verified ? "Yes" : "No"} */}
                                                </label>
                                              </div>

                                              </div>

                                              <div className="col-4 col-md-4 col-sm-4">
                                                <label htmlFor={`EditEventWeRecommend-${eventId}`} className="form-label">
                                                  We Recommend 
                                                </label>
                                                  <div className="form-check form-switch">
                                                    <input
                                                      className="form-check-input"
                                                      type="checkbox"
                                                      id={`EditEventWeRecommend-${eventId}`}
                                                      checked={WeRecommend}
                                                      onChange={(e) => setWeRecommend(e.target.checked)}
                                                    />
                                                    <label
                                                      className="form-check-label"
                                                      htmlFor={`EditEventWeRecommend-${eventId}`}
                                                    >
                                                      {/* {WeRecommend ? "Yes" : "No"} */}
                                                    </label>
                                                  </div>
                                              </div>

                                            </div>
                                           </div>
                                        )}

                                          <div className="row mb-3">
                                            <div className="col-md-6 mb-3">
                                              <label htmlFor={`EditEventName-${eventId}`} className="form-label">
                                                Event Name <span className="text-danger-small">Required</span>
                                              </label>
                                              <input type="text" className={`form-control${eventNameError ? " is-invalid" : ""}`} id={`EditEventName-${eventId}`} defaultValue={event.EVENT_NAME}/>
                                              {eventNameError && <div className="text-danger mt-1">{eventNameError}</div>}
                                              <p className=" small form-help-text ">Enter the name of the event, to change the URL for the event please contact support.</p>
                                            </div>
                                          </div>

                                          <div className="row mb-3">
                                            <div className="col-12 mb-3">
                                              <label className="form-label">
                                                Description
                                              </label>
                                              <div className="editor-wrapper">
                                                {/* Toolbar */}
                                                <div className="editor-toolbar">
                                                  <EditMarkButton format="bold" label='<i class="bi bi-type-bold"></i>' />
                                                  <EditMarkButton format="italic" label='<i class="bi bi-type-italic"></i>' />
                                                  <EditMarkButton format="underline" label='<i class="bi bi-type-underline"></i>' />
                                                  <EditBlockButton format="heading-one" label='<i class="bi bi-type-h1"></i>' />
                                                  <EditBlockButton format="heading-two" label='<i class="bi bi-type-h2"></i>' />
                                                  <EditBlockButton format="heading-three" label='<i class="bi bi-type-h3"></i>' />
                                                  <EditBlockButton format="paragraph" label='<i class="bi bi-paragraph"></i>' />
                                                  <EditBlockButton format="bulleted-list" label='<i class="bi bi-list-task"></i>' />
                                                  <EditBlockButton format="numbered-list" label='<i class="bi bi-list-ol"></i>' />
                                                </div>
                                                {/* Editor */}
                                                <div className="editor-container">
                                                  <Slate key={`slate-${event?.id}-${showEdit}`} editor={editEditor} initialValue={editEditorValue && editEditorValue.length > 0 ? editEditorValue : [{ type: 'paragraph', children: [{ text: '' }] }]} onChange={setEditEditorValue}>
                                                    <Editable 
                                                      placeholder="Type your event description here..."
                                                      style={{ outline: 'none', minHeight: '120px', fontFamily: 'inherit', fontSize: 'inherit' }}
                                                      renderElement={({ attributes, children, element }) => {
                                                        switch (element.type) {
                                                          case 'heading-one':
                                                            return <h1 {...attributes} style={{ fontSize: '24px', fontWeight: 'bold', margin: '10px 0' }}>{children}</h1>;
                                                          case 'heading-two':
                                                            return <h2 {...attributes} style={{ fontSize: '20px', fontWeight: 'bold', margin: '10px 0' }}>{children}</h2>;
                                                          case 'heading-three':
                                                            return <h3 {...attributes} style={{ fontSize: '16px', fontWeight: 'bold', margin: '10px 0'}}>{children}</h3>;
                                                          case 'bulleted-list':
                                                            return <li {...attributes} style={{ marginLeft: '20px', listStyleType: 'disc' }}>{children}</li>;
                                                          case 'numbered-list':
                                                            return <li {...attributes} style={{ marginLeft: '20px', listStyleType: 'decimal' }}>{children}</li>;
                                                          default:
                                                            return <p {...attributes}>{children}</p>;
                                                        }
                                                      }}
                                                      renderLeaf={({ attributes, children, leaf }) => {
                                                        if (leaf.bold) children = <strong>{children}</strong>;
                                                        if (leaf.italic) children = <em>{children}</em>;
                                                        if (leaf.underline) children = <u>{children}</u>;
                                                        return <span {...attributes}>{children}</span>;
                                                      }}
                                                    />
                                                  </Slate>
                                                </div>
                                              </div>

                                              <p className=" small form-help-text ">Write a description for the event that will be displayed to attendees.</p>
                                            </div>
                                          </div>

                                            <div className="row mb-5">         
                                              <div className="col-12">
                                                <label htmlFor={`EditEventPublic-${eventId}`} className="form-label">
                                                  Event Public 
                                                </label>

                                              <div className="form-check form-switch">
                                                <input
                                                  className="form-check-input"
                                                  type="checkbox"
                                                  id={`EditEventPublic-${eventId}`}
                                                  checked={Public}
                                                  onChange={(e) => setPublic(e.target.checked)}
                                                />
                                                <label
                                                  className="form-check-label"
                                                  htmlFor={`EditEventPublic-${eventId}`}
                                                >
                                                  {/* {Public ? "Yes" : "No"} */}
                                                </label>
                                              </div>
                                              <p className=" small form-help-text ">Public events are visible to everyone. Private events require a link.</p>
                                              </div>



                                              <div className="col-12">
                                                <label htmlFor={`EditEventRequestAccess-${eventId}`} className="form-label">
                                                  Request Access 
                                                </label>
                                               <div className="form-check form-switch">
                                                <input
                                                  className="form-check-input"
                                                  type="checkbox"
                                                  id={`EditEventRequestAccess-${eventId}`}
                                                  checked={RequestAccess}
                                                  onChange={(e) => setRequestAccess(e.target.checked)}
                                                />
                                                <label
                                                  className="form-check-label"
                                                  htmlFor={`EditEventRequestAccess-${eventId}`}
                                                >
                                                  {/* {RequestAccess ? "Yes" : "No"} */}
                                                </label>
                                              </div>
                                              <p className=" small form-help-text ">Request access allows attendees to request permission to join the event and to see all event data.</p>

                                              </div>


                                              <div className="col-12">
                                                <label htmlFor={`EditEventDisplayRecommendations-${eventId}`} className="form-label">
                                                      Allow Reactions
                                                </label>
                                               <div className="form-check form-switch">
                                                <input
                                                  className="form-check-input"
                                                  type="checkbox"
                                                  id={`EditEventDisplayRecommendations-${eventId}`}
                                                  checked={DisplayRecommendations}
                                                  onChange={(e) => setDisplayRecommendations(e.target.checked)}
                                                />
                                                {/* <label
                                                  className="form-check-label"
                                                  htmlFor={`EditEventDisplayRecommendations-${eventId}`}
                                                >
                                                   {DisplayRecommendations ? "Yes" : "No"} 
                                                </label> */}
                                               
                                              </div>
                                                  <p className=" small form-help-text ">Allow users to recommend the event.</p>
                                              </div>


                                            </div>

                                          <h4 className="mt-5">General Pricing</h4>
                                          <p className=" small form-help-text mt-0 mb-5 divider">For more pricing options visit the 'Pricing & Perks' tab.</p>

                                          <div className="row mb-3 ">
                                            <div className="col-md-3 col-sm-6 mb-3">
                                              <label htmlFor={`EditEventPrice-${eventId}`} className="form-label">
                                                General Ticket
                                              </label>
                                              <input type="text" className={`form-control mb-2 ${eventPriceError ? " is-invalid" : ""}`} id={`EditEventPrice-${eventId}`} defaultValue={event.EVENT_PRICE}/>
                                              {eventPriceError && <div className="text-danger mt-1">{eventPriceError}</div>}

                                            </div>

                                            <div className="col-md-3 col-sm-6 mb-3">
                                              <label htmlFor={`EditEventPriceMin-${eventId}`} className="form-label">Minimum Price</label>
                                              <input type="text" className="form-control" id={`EditEventPriceMin-${eventId}`} defaultValue={event.EVENT_PRICEMIN}/>
                                            </div>

                                              <div className="col-md-3 col-sm-6 mb-3">
                                                <label className="form-label">
                                                Event Sold Out
                                                </label>

                                                <div className="form-check form-switch">
                                                  <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id={`EditEventSoldOut-${eventId}`}
                                                    checked={soldOut}
                                                    onChange={(e) => setSoldOut(e.target.checked)}
                                                  />
                                                  <label
                                                    className="form-check-label"
                                                    htmlFor={`EditEventSoldOut-${eventId}`}
                                                  >
                                                    {/* {soldOut ? "Yes" : "No"} */}
                                                  </label>
                                                  
                                                </div>
                                            </div>

                                          </div>

                                          <h4 className="mt-5">Event Date & Time</h4>
                                          <p className=" small form-help-text mt-0 mb-5 divider">Select the date and time & how many days the event will last.</p>

                                          <div className="row mb-3">

                                            <div className="col-md-3 col-sm-12 mb-3">
                                                <label className="form-label">Event Date</label>
                                                <div className="d-flex gap-2">
                                                  <select className="form-select" defaultValue={event.EVENT_DATE?.split("/")[0] || ""} id={`EditEventDay-${eventId}`}>
                                                    {Array.from({ length: 31 }, (_, i) => (
                                                      <option key={i + 1} value={String(i + 1).padStart(2, "0")}>
                                                        {String(i + 1).padStart(2, "0")}
                                                      </option>
                                                    ))}
                                                  </select>

                                                  <select className="form-select" defaultValue={event.EVENT_DATE?.split("/")[1] || ""} id={`EditEventMonth-${eventId}`}
                                                  >
                                                    {Array.from({ length: 12 }, (_, i) => {
                                                      const val = String(i + 1).padStart(2, "0");
                                                      return (
                                                        <option key={val} value={val}>
                                                          {val}
                                                        </option>
                                                      );
                                                    })}
                                                  </select>

                                                  <select className="form-select" defaultValue={event.EVENT_DATE?.split("/")[2] || "2025"} id={`EditEventYear-${eventId}`}>
                                                    {[2026, 2027].map((year) => (
                                                      <option key={year} value={year}>
                                                        {year}
                                                      </option>
                                                    ))}
                                                  </select>
                                                </div>
                                            </div>

                                            <div className="col-md-3 col-sm-6 mb-3">
                                                <label className="form-label">Start Time</label>
                                                <div className="d-flex gap-2">
                                                  <select className="form-select" defaultValue={
                                                      event.EVENT_STARTTIME
                                                        ? event.EVENT_STARTTIME.split(":")[0].padStart(2, "0")
                                                        : "00"
                                                    }
                                                    id={`EditEventStartHour-${eventId}`}
                                                  >
                                                    {Array.from({ length: 24 }, (_, i) => (
                                                      <option key={i} value={i.toString().padStart(2, "0")}>
                                                        {i.toString().padStart(2, "0")}
                                                      </option>
                                                    ))}
                                                  </select>

                                                  <select className="form-select" defaultValue={
                                                      event.EVENT_STARTTIME
                                                        ? event.EVENT_STARTTIME.split(":")[1].padStart(2, "0")
                                                        : "00"
                                                    }
                                                    id={`EditEventStartMinute-${eventId}`}
                                                  >
                                                    {Array.from({ length: 60 }, (_, i) => (
                                                      <option key={i} value={i.toString().padStart(2, "0")}>
                                                        {i.toString().padStart(2, "0")}
                                                      </option>
                                                    ))}
                                                  </select>
                                                </div>
                                            </div>
                                              

                                            <div className="col-md-3 col-sm-6 mb-3">
                                                <label className="form-label">End Time</label>
                                                <div className="d-flex gap-2">
                                                  <select className="form-select" defaultValue={
                                                      event.EVENT_ENDTIME
                                                        ? event.EVENT_ENDTIME.split(":")[0].padStart(2, "0")
                                                        : "00"
                                                    }
                                                    id={`EditEventEndHour-${eventId}`}>
                                                    {Array.from({ length: 24 }, (_, i) => (
                                                      <option key={i} value={i.toString().padStart(2, "0")}>
                                                        {i.toString().padStart(2, "0")}
                                                      </option>
                                                    ))}
                                                  </select>

                                                  <select className="form-select" defaultValue={
                                                      event.EVENT_ENDTIME
                                                        ? event.EVENT_ENDTIME.split(":")[1].padStart(2, "0")
                                                        : "00"
                                                    }
                                                    id={`EditEventEndMinute-${eventId}`}>
                                                    {Array.from({ length: 60 }, (_, i) => (
                                                      <option key={i} value={i.toString().padStart(2, "0")}>
                                                        {i.toString().padStart(2, "0")}
                                                      </option>
                                                    ))}
                                                  </select>
                                                </div>
                                            </div>
                                   
                                          </div>

                                          <div className="row mb-3">
                                            <div className="col-md-4 col-sm-12 mb-3">
                                              <label
                                                htmlFor={`editEventExtraDays-${eventId}`}
                                                className="form-label"
                                              >
                                                Multiple Days: <strong>{extraDays}</strong>
                                              </label>

                                              <input
                                                type="range"
                                                className="form-range"
                                                id={`editEventExtraDays-${eventId}`}
                                                min={0}
                                                max={4}
                                                step={1}
                                                value={extraDays}
                                                onChange={(e) => setExtraDays(Number(e.target.value))}
                                              />
                                              <p className="small form-help-text">If this event is over multiple days, use this slider to indicate how many extra days it spans.</p>
                                            </div>
                                          </div>


                                          <h4 className="mt-5">External Site</h4>
                                          <p className=" small form-help-text mt-0 mb-5 divider"> Link to website and ticket site for the event.</p>
                                          
                                          <div className="row mb-3">
                                            <div className="col-md-4 col-sm-12 mb-3">
                                              <label htmlFor={`EditEventSite-${eventId}`} className="form-label">
                                                Website
                                              </label>
                                              <input type="url" className="form-control" id={`EditEventSite-${eventId}`} defaultValue={event.EVENT_SITE}/>
                                            </div>
                                        
                                            <div className="col-md-4 col-sm-12 mb-3">
                                              <label htmlFor={`EditEventTicketSite-${eventId}`} className="form-label">
                                                Ticket Site
                                              </label>
                                              <input type="url" className="form-control" id={`EditEventTicketSite-${eventId}`} defaultValue={event.EVENT_TICKETSITE}/>
                                            </div>
                                          </div>

                                          <h4 className="mt-5">Location</h4>
                                          <p className=" small form-help-text mt-0 mb-5 divider ">Please enter the location and postcode for the event.</p>
                                          

                                          <div className="row mb-3"> 
                                            
                                            <div className="col-md-3 col-sm-12 mb-3">
                                              <label htmlFor={`EditEventLocation-${eventId}`} className="form-label">
                                                Location <span className="text-danger-small">Required</span>
                                              </label>
                                              <input type="text" className={`form-control${eventLocationError ? " is-invalid" : ""}`} id={`EditEventLocation-${eventId}`} defaultValue={event.EVENT_LOCATION}/>
                                              {eventLocationError && <div className="text-danger mt-1">{eventLocationError}</div>}
                                            </div>

                                            <div className="col-md-3 col-sm-12 mb-3">
                                              <label htmlFor={`EditEventPostcode-${eventId}`} className="form-label">
                                                Postcode <span className="text-danger-small">Required</span>
                                              </label>
                                              <input type="text" className={`form-control${eventPostcodeError ? " is-invalid" : ""}`} id={`EditEventPostcode-${eventId}`} defaultValue={event.EVENT_POSTCODE}/>
                                              {eventPostcodeError && <div className="text-danger mt-1">{eventPostcodeError}</div>}
                                            </div>

                                          </div>

                                          <h4 className="mt-5">Photography</h4>
                                          <p className=" small form-help-text mt-0 mb-5 divider"> This site only exists because we wanted to keep track of events for photography so please provide accurate information regarding photography & drone permissions.</p>

                                          <div className="row mb-3"> 
                                            
                                            <div className="col-md-3 col-sm-12 mb-3">
                                              <label htmlFor={`EditEventPhotography-${eventId}`} className="form-label">
                                                Photography 
                                              </label>
                                                   <select 
                                                      className="form-select" 
                                                      defaultValue={event.EVENT_PHOTOGRAPHYALLOWED}
                                                      id={`EditEventPhotography-${eventId}`} 
                                                    >
                                                      <option value="N">No</option>
                                                      <option value="Y">Yes (General Public & Media)</option>
                                                      <option value="M">Media Only </option>
                                                    </select>
                                            </div>

                                            <div className="col-md-3 col-sm-12 mb-3">
                                              <label htmlFor={`EditEventDrones-${eventId}`} className="form-label">
                                                Drones 
                                              </label>
                                                   <select 
                                                      className="form-select" 
                                                      defaultValue={event.EVENT_DRONESALLOWED}
                                                      id={`EditEventDrones-${eventId}`} 
                                                    >
                                                      <option value="N">No</option>
                                                      <option value="Y">Yes (General Public & Media)</option>
                                                      <option value="M">Media Only </option>
                                                    </select>
                                            </div>

                                          </div>


                                          <h4>Banner Image</h4>
                                          <p className=" small form-help-text mt-0 mb-5 divider"> Select a banner image for the event.</p>


                                          <div className="row mb-3">   

                                                  <div className="col-md-6">
                                
                                                    <div className="dropdown-container">
                                                                    
                                                      <div className="preview">
                                                        {(() => {
                                                          const selected = options.find((o) => o.value === selectedImage);

                                                          return (
                                                            <>
                                                             {selected && selected.img === '' ? (
                                                              <div className={`event-image-fallback event-image-${event.EVENT_TYPE} w-full object-cover max-sm:h-50 sm:aspect-[3/1]`}>
                                                               
                                                              </div>
                                                             ) :   <>
                                                             {selected && <img src={selected.img} alt={selected.label}  loading="lazy"/>}
                                                             </>
                                                             }
                                                            </>
                                                          );
                                                        })()}
                                                      </div>
                                                    </div>
                                                  </div>
                                                                                  
                                                      <div className="col-md-6">
                                                        <label htmlFor={`EditEventImageLocation-${eventId}`} className="form-label">
                                                          Image 
                                                        </label>
                                                          <select
                                                          value={selectedImage}
                                                            onChange={(e) => setSelectedImage(e.target.value)}
                                                            className="form-select mb-4">
                                                            {options.map((item) => (
                                                              <option key={item.value} value={item.value}>
                                                                {item.label}
                                                              </option>
                                                            ))}
                                                          </select>
                                                      </div>

                                                  <div className="col-md-6 mb-3">
                                                    <div className="row">
                
                                                      {(() => {
                                                          const selected = options.find((o) => o.value === selectedImage);

                                                          return (
                                                              <div className="col-md-12">
                                                            <div className="mb-2 desktop-view-button">
                                                              <div className="p-2 rounded-md">
                                                                      

                                                                  {selected && selected.label === 'No Presets Available' ? (
                                                                    <p className="form-help-text mb-3">
                                                                      Sorry this event doesn't have any presets available.
                                                                    </p>
                                                                  ) : <p className="form-help-text mb-3">
                                                                      Preset images are provided by our contributing photographers. All image rights belong to the individuals listed below.
                                                                    </p>
                                                                  }
                                                            
                                                                {selected && selected.photographer && (
                                                                  <p className="form-help-text mb-0">
                                                                    Photographer: <span className="text-primary"> {selected.photographer} </span>
                                                                  </p>
                                                                )}
                                                                
                                                                {selected && selected.photoevent && (
                                                                  <p className="form-help-text mb-0">
                                                                    Event: <span className="text-primary"> {selected.photoevent} </span>
                                                                  </p>
                                                                )}
                                                                
                                                                {selected && selected.photosite && (
                                                                  <p className="form-help-text mb-0">
                                                                    Photographer Website: <a href={selected.photosite} target="_blank" className="modal-ticket-desc" rel="noreferrer"> Vist Site</a>
                                                                  </p>
                                                                )}

                                                                    <p className="form-help-text my-3">
                                                                      Want to add a custom banner for your event? feel free to contact Support.{" "}
                                                                      <Link className="text-primary" to="/Support">
                                                                        contact us
                                                                      </Link>.
                                                                    </p>

                                                                </div>
                                                              </div>
                                                              </div>
                                                          );
                                                      })()}

                                                        
                                                    </div>
                                                
                                                  </div>
                                          </div>



                                <div className="filter-section"></div>
                                
                                <div className="modal-footer justify-content-between mt-3 border-0">
                                  <button type="button" className="btn-tertiary-outline" onClick={() => setShowEdit(false)}> Cancel</button>

                                  <button type="button" className="btn btn-primary" onClick={() => handleUpdateEvent(eventId)}> Save Changes
                                  </button>
                                </div>

                          </div>

                      </div>

                      <div className="tab-pane fade" id="nav-profile" role="tabpanel" aria-labelledby="nav-profile-tab">
                        <div className="">

                                <div className="row justify-content-between align-items-end mt-5 mb-0">
                                  <div className="col-sm-12 ">
                                      <h2>Add Warning</h2>
                                      <p className="card-text text-gray-500">Here you can add an alert to the page so users are informed of important information.</p>
                                  </div>
                                </div>

                      
                                        {tokList.length > 0 ? (
                                        <div className="mt-5">
                                          {tokList.map((tok) => (
                                            <div key={tok.id} className="row justify-content-between align-items-center mb-2">
                                              {editingTokId === tok.id ? (
                                                <>
                                                  <div className="col-md-3 col-sm-12 mb-2">
                                                    <input 
                                                      type="text" 
                                                      className="form-control" 
                                                      value={editTokHeader}
                                                      onChange={(e) => setEditTokHeader(e.target.value)}
                                                      placeholder="Header"
                                                    />
                                                  </div>
                                                  <div className="col-md-3 col-sm-12 mb-2">
                                                    <input 
                                                      type="text" 
                                                      className="form-control" 
                                                      value={editTokPara}
                                                      onChange={(e) => setEditTokPara(e.target.value)}
                                                      placeholder="Message"
                                                    />
                                                  </div>
                                                  <div className="col-md-2 col-sm-12 mb-2">
                                                    <select 
                                                      className="form-select" 
                                                      value={editTokType}
                                                      onChange={(e) => setEditTokType(e.target.value)}
                                                    >
                                                      <option value="INFO">Info</option>
                                                      <option value="WARNING">Warning</option>
                                                      <option value="DANGER">Danger</option>
                                                    </select>
                                                  </div>
                                                  <div className="col-md-2 col-sm-12 text-md-end text-sm-start mb-2">
                                                    <button className="btn btn-sm btn-success me-1" onClick={() => handleSaveTok(tok.id)}>
                                                      Save
                                                    </button>
                                                    <button className="btn btn-sm btn-secondary" onClick={() => setEditingTokId(null)}>
                                                      Cancel
                                                    </button>
                                                  </div>
                                                </>
                                              ) : (
                                                <>
                                                  <div className="col-md-3 col-sm-12 mb-3">
                                                    <input type="text" className="form-control" value={tok.HEADER} disabled/>
                                                  </div>
                                                  <div className="col-md-3 col-sm-12 mb-3">
                                                    <input type="text" className="form-control" value={tok.PARA} disabled/>
                                                  </div>
                                                  <div className="col-md-2 col-sm-12 mb-3">
                                                    <input type="text" className="form-control" value={tok.TYPE} disabled/>
                                                  </div>

                                                  <div className="col-md-2 col-sm-12 text-md-end text-sm-start">
                                                    <button className="btn btn-sm btn-primary me-1" onClick={() => handleEditTok(tok)}>
                                                      Edit
                                                    </button>
                                                    <button className="btn btn-danger btn-sm" onClick={() => handleDeleteTok(tok.id)}>
                                                      Delete
                                                    </button>
                                                  </div>
                                                </>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      ) : (
                                        <>
                                        </>
                                      )}


                                          <div className="row justify-content-between align-items-end mt-5 mb-0">

                                            <div className="col-md-3 col-sm-12 mb-3">
                                              <label className="form-label">Header</label>
                                              <input type="text" className="form-control" placeholder="..." value={newTokHeader} onChange={(e) => setNewTokHeader(e.target.value)}/>
                                            </div>

                                            <div className="col-md-3 col-sm-12 mb-3">
                                              <label className="form-label">Message</label>
                                              <input type="text" className="form-control" placeholder="..." value={newTokPara} onChange={(e) => setNewTokPara(e.target.value)}/>
                                            </div>

                                            <div className="col-md-3 col-sm-12 mb-3">
                                              <label className="form-label">Type</label>
                                              <select className="form-select" value={newTokType} onChange={(e) => setNewTokType(e.target.value)}>
                                                <option value="" disabled>Select type</option>
                                                <option value="INFO">Info (Blue) </option>
                                                <option value="WARNING">Warning (orange)</option> 
                                                <option value="DANGER">Danger (Red) </option>
                                              </select>
                                            </div>

                                            <div className="col-md-3 col-sm-12 text-md-end text-sm-star mb-3">
                                              <button className="btn btn-primary" onClick={handleAddTok}>
                                                Add Announcement
                                              </button>
                                            </div>

                                          </div>

                          </div>
                      </div>

                      <div className="tab-pane fade" id="nav-schedu" role="tabpanel" aria-labelledby="nav-schedu-tab">
                        <div className="">

                                  <div className="row justify-content-between align-items-end mt-5 mb-0">
                                    <div className="col-sm-12 ">
                                        <h2>Add Schedule</h2>
                                        <p className="card-text text-gray-500">Add a Timeline of events to the page.</p>
                                    </div>
                                  </div>

  
                                          <div className="row justify-content-between align-items-end mt-5">
                                            {/* Create Schedule Title Section */}
                                            <div className="col-12 mb-4 ">
                                              <h5 className="mb-3 card-text text-gray-500">Create New Schedule</h5>
                                              <div className="row align-items-end">
                                                <div className="col-md-6 col-sm-12 mb-3">
                                                  <label className="form-label">Schedule Title</label>
                                                  <input 
                                                    type="text" 
                                                    className="form-control" 
                                                    placeholder="..." 
                                                    value={newScheduleTitle} 
                                                    onChange={(e) => setNewScheduleTitle(e.target.value)}
                                                    onKeyPress={(e) => e.key === 'Enter' && handleCreateScheduleTitle()}
                                                  />
                                                </div>
                                                <div className="col-md-2 col-sm-12 mb-3">
                                                  <button className="btn btn-primary " onClick={handleCreateScheduleTitle}>
                                                    Create Schedule
                                                  </button>
                                                </div>
                                              </div>
                                         

                                         <h5 className="mb-3 card-text text-gray-500">Select Schedule</h5>
                                            {/* Schedule Tabs Navigation - Top */}
                                            {schedulesTitles.length > 0 && (

                                              <div className="col-12 mb-4">
                                                <ul className="nav nav-tabs" role="tablist">
                                                  {schedulesTitles.map((schedule) => (
                                                    <li key={schedule.id} className="nav-item" role="presentation">
                                                      <button
                                                        className={`nav-link ${selectedScheduleTitle === schedule.id ? 'active' : ''}`}
                                                        id={`schedule-tab-${schedule.id}`}
                                                        type="button"
                                                        role="tab"
                                                        aria-controls={`schedule-pane-${schedule.id}`}
                                                        aria-selected={selectedScheduleTitle === schedule.id}
                                                        onClick={() => setSelectedScheduleTitle(schedule.id)}
                                                      >
                                                        <div className="d-flex align-items-center gap-2">
                                                          <span>{schedule.name}</span>
                                                          <i 
                                                            className="bi bi-trash3 small" 
                                                            style={{ cursor: 'pointer', fontSize: '0.9rem' }}
                                                            onClick={(e) => {
                                                              e.stopPropagation();
                                                              if (window.confirm(`Delete "${schedule.name}" and all its items?`)) {
                                                                handleDeleteScheduleTab(schedule.id);
                                                              }
                                                            }}
                                                            title="Delete this schedule"
                                                          />
                                                        </div>
                                                      </button>
                                                    </li>
                                                  ))}
                                                </ul>
                                              </div>

                                            )}

                                            {/* Step 3: Add Items to Selected Schedule */}
                                            {selectedScheduleTitle && (
                                              <div className="col-12 mb-4 ">
                                                <h5 className="mb-3">Add Item to "{schedulesTitles.find(s => s.id === selectedScheduleTitle)?.name || 'Schedule'}"</h5>
                                                <div className="row align-items-end">
                                                  <div className="col-md-2 col-sm-12 mb-3">
                                                    <label className="form-label">Type</label>
                                                    <input 
                                                      type="text" 
                                                      className="form-control" 
                                                      placeholder="..." 
                                                      value={newScheduleType} 
                                                      onChange={(e) => setNewScheduleType(e.target.value)}
                                                    />
                                                  </div>

                                                  <div className="col-md-2 col-sm-12 mb-3">
                                                    <label className="form-label">Start Time</label>
                                                    <div className="d-flex gap-2">
                                                      <select className="form-select" value={newScheduleStartHour} onChange={(e) => setNewScheduleStartHour(e.target.value)}>
                                                        {Array.from({ length: 24 }, (_, i) => (
                                                          <option key={i} value={i.toString().padStart(2, "0")}>
                                                            {i.toString().padStart(2, "0")}
                                                          </option>
                                                        ))}
                                                      </select>
                                                      <span className="align-self-end">:</span>
                                                      <select className="form-select" value={newScheduleStartMinute} onChange={(e) => setNewScheduleStartMinute(e.target.value)}>
                                                        {Array.from({ length: 60 }, (_, i) => (
                                                          <option key={i} value={i.toString().padStart(2, "0")}>
                                                            {i.toString().padStart(2, "0")}
                                                          </option>
                                                        ))}
                                                      </select>
                                                    </div>
                                                  </div>

                                                  {newScheduleHasEndTime && (
                                                    <div className="col-md-2 col-sm-12 mb-3">
                                                      <label className="form-label">End Time</label>
                                                      <div className="d-flex gap-2">
                                                        <select className="form-select" value={newScheduleEndHour} onChange={(e) => setNewScheduleEndHour(e.target.value)}>
                                                          {Array.from({ length: 24 }, (_, i) => (
                                                            <option key={i} value={i.toString().padStart(2, "0")}>
                                                              {i.toString().padStart(2, "0")}
                                                            </option>
                                                          ))}
                                                        </select>
                                                        <span className="align-self-end">:</span>
                                                        <select className="form-select" value={newScheduleEndMinute} onChange={(e) => setNewScheduleEndMinute(e.target.value)}>
                                                          {Array.from({ length: 60 }, (_, i) => (
                                                            <option key={i} value={i.toString().padStart(2, "0")}>
                                                              {i.toString().padStart(2, "0")}
                                                            </option>
                                                          ))}
                                                        </select>
                                                      </div>
                                                    </div>
                                                  )}

                                                  <div className="col-md-1 col-sm-12 mb-3">
                                                    <div className="form-check form-switch">
                                                      <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        id="scheduleHasEndTime"
                                                        checked={newScheduleHasEndTime}
                                                        onChange={(e) => setNewScheduleHasEndTime(e.target.checked)}
                                                      />
                                                      <label className="form-check-label" htmlFor="scheduleHasEndTime">
                                                        End
                                                      </label>
                                                    </div>
                                                  </div>

                                                  <div className="col-md-2 col-sm-12 mb-3">
                                                    <label className="form-label">Where</label>
                                                    <input 
                                                      type="text" 
                                                      className="form-control" 
                                                      placeholder="..." 
                                                      value={newScheduleLocation} 
                                                      onChange={(e) => setNewScheduleLocation(e.target.value)}
                                                    />
                                                  </div>

                                                  <div className="col-md-1 col-sm-12 mb-3">
                                                    <button className="btn btn-primary w-100" onClick={handleAddSchedule}>
                                                      Add
                                                    </button>
                                                  </div>
                                                </div>
                                              </div>
                                            )}

                                          </div>

                                          {scheduleList.length > 0 && selectedScheduleTitle ? (
                                                <div className="col-12 mb-5">
                                                  <div className="p-3">
                                                    <div className="mb-4">
                                                      <h4 className="mb-0">{schedulesTitles.find(s => s.id === selectedScheduleTitle)?.name || "Schedule"}</h4>

                                                    </div>
                                                    
                                                    {scheduleList.filter(item => item.parentScheduleId === selectedScheduleTitle).length === 0 ? (
                                                      <></>
                                                    ) : (
                                                      <div className="row">
                                                        <div className="row schedule-header  mb-3">
                                                          <div className="col-2"><strong>Type</strong></div>
                                                          <div className="col-3"><strong>Time</strong></div>
                                                          <div className="col-3"><strong>Location</strong></div>
                                                          <div className="col-4"><strong>Actions</strong></div>
                                                        </div>
                                                        {scheduleList
                                                          .filter(item => item.parentScheduleId === selectedScheduleTitle)
                                                          .map((item) => (
                                                            <div key={item.id} className="col-12 mb-2">
                                                              {editingScheduleId === item.id ? (
                                                                <div className="row g-2 p-2 ">
                                                                  <div className="col-md-2 col-sm-12">
                                                                    <input 
                                                                      type="text" 
                                                                      className="form-control form-control-sm" 
                                                                      value={editScheduleType}
                                                                      onChange={(e) => setEditScheduleType(e.target.value)}
                                                                      placeholder="Type"
                                                                    />
                                                                  </div>
                                                                  <div className="col-md-3 col-sm-12">
                                                                    <label className="form-label small d-md-none">Start Time</label>
                                                                    <div className="d-flex gap-1">
                                                                      <select className="form-select form-select-sm" value={editScheduleStartHour} onChange={(e) => setEditScheduleStartHour(e.target.value)}>
                                                                        {Array.from({ length: 24 }, (_, i) => (
                                                                          <option key={i} value={i.toString().padStart(2, "0")}>
                                                                            {i.toString().padStart(2, "0")}
                                                                          </option>
                                                                        ))}
                                                                      </select>
                                                                      <span className="align-self-center">:</span>
                                                                      <select className="form-select form-select-sm" value={editScheduleStartMinute} onChange={(e) => setEditScheduleStartMinute(e.target.value)}>
                                                                        {Array.from({ length: 60 }, (_, i) => (
                                                                          <option key={i} value={i.toString().padStart(2, "0")}>
                                                                            {i.toString().padStart(2, "0")}
                                                                          </option>
                                                                        ))}
                                                                      </select>
                                                                      {editScheduleHasEndTime && (
                                                                        <>
                                                                          <span className="align-self-center">-</span>
                                                                          <select className="form-select form-select-sm" value={editScheduleEndHour} onChange={(e) => setEditScheduleEndHour(e.target.value)}>
                                                                            {Array.from({ length: 24 }, (_, i) => (
                                                                              <option key={i} value={i.toString().padStart(2, "0")}>
                                                                                {i.toString().padStart(2, "0")}
                                                                              </option>
                                                                            ))}
                                                                          </select>
                                                                          <span className="align-self-center">:</span>
                                                                          <select className="form-select form-select-sm" value={editScheduleEndMinute} onChange={(e) => setEditScheduleEndMinute(e.target.value)}>
                                                                            {Array.from({ length: 60 }, (_, i) => (
                                                                              <option key={i} value={i.toString().padStart(2, "0")}>
                                                                                {i.toString().padStart(2, "0")}
                                                                              </option>
                                                                            ))}
                                                                          </select>
                                                                        </>
                                                                      )}
                                                                    </div>
                                                                  </div>

                                                                  <div className="col-md-3 col-sm-12">
                                                                    <input 
                                                                      type="text" 
                                                                      className="form-control form-control-sm" 
                                                                      value={editScheduleLocation}
                                                                      onChange={(e) => setEditScheduleLocation(e.target.value)}
                                                                      placeholder="Location"
                                                                    />
                                                                  </div>
                                                                  <div className="col-md-4 col-sm-12">
                                                                    <div className="d-flex gap-1">
                                                                      <div className="form-check form-switch me-2">
                                                                        <input
                                                                          className="form-check-input"
                                                                          type="checkbox"
                                                                          id={`editScheduleHasEndTime-${item.id}`}
                                                                          checked={editScheduleHasEndTime}
                                                                          onChange={(e) => setEditScheduleHasEndTime(e.target.checked)}
                                                                        />
                                                                        <label className="form-check-label small" htmlFor={`editScheduleHasEndTime-${item.id}`}>
                                                                          End Time
                                                                        </label>
                                                                      </div>
                                                                      <button className="btn btn-sm btn-success me-1" onClick={() => handleSaveSchedule(item.id, item.parentScheduleId)}>
                                                                        Save
                                                                      </button>
                                                                      <button className="btn btn-sm btn-secondary" onClick={() => setEditingScheduleId(null)}>
                                                                        Cancel
                                                                      </button>
                                                                    </div>
                                                                  </div>
                                                                </div>
                                                              ) : (
                                                                <div className="row align-items-center py-2">
                                                                  <div className="col-md-2 col-sm-12">
                                                                    {item.SCH_NAME}
                                                                  </div>
                                                                  <div className="col-md-3 col-sm-12">
                                                                    <span className="">{item.SCH_TIME}</span>
                                                                  </div>
                                                                  <div className="col-md-3 col-sm-12">
                                                                    <small>{item.SCH_LOCATION || "—"}</small>
                                                                  </div>
                                                                  <div className="col-md-4 col-sm-12">
                                                                    <button className="btn btn-sm btn-primary me-2" onClick={() => handleEditSchedule(item)}>
                                                                      Edit
                                                                    </button>
                                                                    <button className="btn btn-sm btn-danger" onClick={() => handleDeleteSchedule(item.id, item.parentScheduleId)}>
                                                                      Delete
                                                                    </button>
                                                                  </div>
                                                                </div>
                                                              )}
                                                            </div>
                                                          ))}
                                                      </div>
                                                    )}
                                                  </div>
                                                </div>
                                          ) : scheduleList.length > 0 && !selectedScheduleTitle ? (
                                                <></>
                                          ) : (
                                                <></>
                                          )}

                                         </div>
                          
                        </div>
                      </div>

                      <div className="tab-pane fade" id="nav-contact" role="tabpanel" aria-labelledby="nav-contact-tab">
                        <div className="">
                                              <div className="row justify-content-between align-items-end mt-5 mb-0">
                                                <div className="col-sm-12 ">
                                                    <h2>Frequently Asked Questions</h2>
                                                    <p className="card-text text-gray-500">Add FAQS to the page to help users understand the event better.</p>
                                                </div>
                                              </div>

                                            {faqList.length  > 0 ? (
                                              <div className="mt-5">

                                                {faqList.map((faq) => (
                                                  editingFaqId === faq.id ? (
                                                    <div className="row justify-content-between align-items-end mb-0">
                                                      <div className="col-md-4 col-sm-12 mb-3">
                                                        <input
                                                          key={editFaqQuestion}
                                                          type="text"
                                                          className="form-control"
                                                          value={editFaqQuestion}
                                                          onChange={(e) => setEditFaqQuestion(e.target.value)}
                                                          placeholder="Question"
                                                        />
                                                      </div>

                                                      <div className="col-md-4 col-sm-12 mb-3">
                                                        <input
                                                          key={editFaqAnswer}
                                                          type="text"
                                                          className="form-control"
                                                          value={editFaqAnswer}
                                                          onChange={(e) => setEditFaqAnswer(e.target.value)}
                                                          placeholder="Answer"
                                                        />
                                                      </div>

                                                      <div className="col-md-4 col-sm-12 text-sm-start mb-3">
                                                        <button
                                                          className="btn btn-sm btn-primary me-2"
                                                          onClick={() => handleSaveFaq(faq.id)}
                                                        >
                                                          Save
                                                        </button>
                                                        <button
                                                          className="btn btn-sm btn-outline-danger"
                                                          onClick={() => {
                                                            setEditingFaqId(null);
                                                            setEditFaqQuestion("");
                                                            setEditFaqAnswer("");
                                                          }}
                                                        >
                                                          Cancel
                                                        </button>
                                                      </div>
                                                    </div>
                                                  ) : (
                                                    <div className="row justify-content-between align-items-end mb-0">

                                                      <div className="col-md-4 col-sm-12 mb-3">
                                                        <input type="text" className="form-control" value={faq.QUESTION} disabled/> 
                                                      </div>

                                                      <div className="col-md-4 col-sm-12 mb-3">
                                                        <input type="text" className="form-control" value={faq.ANSWER} disabled/> 
                                                      </div>

                                                      <div className="col-md-4 col-sm-12 text-sm-start mb-3">
                                                        <button
                                                          className="btn btn-sm btn-primary me-1"
                                                          onClick={() => handleEditFaq(faq)}
                                                        >
                                                          Edit
                                                        </button>
                                                        <button className="btn btn-sm btn-danger" onClick={() => handleDeleteFaq(faq.id)}>
                                                          Delete
                                                        </button>
                                                      </div>
                              
                                                    </div>
                                                  )
                                                ))}
                                              </div>
                                            ) : (
                                              <div className=""></div>
                                            )}
 

                                          <div className="row justify-content-between align-items-end mt-5">

                                            <h2>Add FAQ's</h2>
                                            <p className="card-text text-gray-500 mb-5">Add Questions and answers here, you can edit these at any time.</p>

                                            <div className="col-md-4 col-sm-12 mb-3">
                                              <label className="form-label">Question</label>
                                              <input type="text" className="form-control" placeholder="..." value={newQuestion} onChange={(e) => setNewQuestion(e.target.value)}/>
                                            </div>

                                            <div className="col-md-4 col-sm-12 mb-3">
                                              <label className="form-label">Answer</label>
                                              <input type="text" className="form-control" placeholder="..." value={newAnswer} onChange={(e) => setNewAnswer(e.target.value)}/>
                                            </div>

                                            <div className="col-md-4 col-sm-12 text-md-end text-sm-start mb-3">
                                              <button className="btn btn-primary" onClick={handleAddFaq}>
                                                Add FAQ
                                              </button>
                                            </div>

                                          </div>
                        
                        </div>
                      </div>

                      <div className="tab-pane fade" id="nav-whitelist" role="tabpanel" aria-labelledby="nav-whitelist-tab">
                      
                          <div className="row justify-content-between align-items-end mt-5 mb-0">
                            <div className="col-sm-12 ">
                                <h2>Whitelist Users</h2>
                                <p className="card-text text-gray-500">This page is only active if the Event is set to Request access only & is Private.</p>
                            </div>
                          </div>
                                            
                          <div className="row justify-content-between align-items-end mt-5 mb-0">
                            <div className="col-sm-12 ">
                              <h2>Pending Requests</h2>
                              <p className="card-text text-gray-500">Click Accept or Reject to manage access requests.</p>
                              <button className="btn btn-sm btn-primary mb-3" onClick={() => { loadJoinRequests(); loadWhitelistedUsers(); }}>Refresh</button>
                            </div>
                          </div>

                          {joinRequests.length > 0 ? (
                                  <div className="row justify-content-between align-items-end mt-5 mb-0">
                            <div className="col-sm-12 ">
                            <div className="table-responsive">
                              <table className="table table-hover">
                                <thead className="table-light">
                                  <tr>
                                    <th>User Name</th>
                                    <th>Request Date</th>
                                    <th>Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {joinRequests.map((request) => (
                                    <tr key={request.id}>
                                      <td>{request.JRQ_USERNAME}</td>
                                      <td>{new Date(request.JRQ_DATE).toLocaleDateString()}</td>
                                      <td>
                                        <button 
                                          className="btn btn-sm btn-success me-2"
                                          onClick={() => handleApproveRequest(request.id, request.JRQ_USERID, request.JRQ_USERNAME)}
                                        >
                                          <i className="bi bi-check-lg"></i> Accept
                                        </button>
                                        <button 
                                          className="btn btn-sm btn-danger"
                                          onClick={() => handleRejectRequest(request.id, request.JRQ_USERNAME)}
                                        >
                                          <i className="bi bi-x-lg"></i> Reject
                                        </button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                              </div>
                                </div>
                          ) : (
                            <div className="row justify-content-between align-items-end  mb-0">
                            <div className="col-sm-12 ">
                            <p className="text-muted">No pending requests</p>
                            </div>
                            </div>
                          )}

                          <div className="filter-section"></div>  

                          <div className="row justify-content-between align-items-end mt-5 mb-0">
                            <div className="col-sm-12 ">
                                <h2>Approved Users</h2>
                                <p className="card-text text-gray-500">List of users approved to access this event</p>
                            </div>
                          </div>

                          {whitelistedUsers.length > 0 ? (
                          <div className="row justify-content-between align-items-end mt-5 mb-0">
                            <div className="col-sm-12 ">

                            <div className="table-responsive">
                              <table className="table table-hover">
                                <thead className="table-light">
                                  <tr>
                                    <th>User Name</th>
                                    <th>Approved Date</th>
                                    <th>Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {whitelistedUsers.map((user) => (
                                    <tr key={user.id}>
                                      <td>{user.WL_USERNAME}</td>
                                      <td>{new Date(user.WL_DATE_APPROVED).toLocaleDateString()}</td>
                                      <td>
                                        <button 
                                          className="btn btn-sm btn-danger"
                                          onClick={() => handleRemoveFromWhitelist(user.id, user.WL_USERNAME)}
                                        >
                                          <i className="bi bi-trash3"></i> 
                                        </button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                              </div>
                                </div>
                          ) : (
                           <div className="row justify-content-between align-items-end mb-0">
                            <div className="col-sm-12 ">
                             <p className="text-muted">No approved users yet</p>
                            </div>
                            </div>
                          )}

                      </div>

                      <div className="tab-pane fade" id="nav-data" role="tabpanel" aria-labelledby="nav-data-tab">
                      
                          <div className="row justify-content-between align-items-end mt-5 mb-0">
                            <div className="col-sm-12 ">
                                <h2>Data </h2>
                                <p className="card-text text-gray-500">This page contains various statistics and metrics related to the event. This is an early iteration and more data and fine tuning will be added in future updates.</p>
                            </div>
                          </div>

                          <div className="row justify-content-between align-items-stretch mt-5 mb-0 g-3">
                            <div className="col-lg-3 col-md-6">
                              <div className="event-sidebar p-4 h-100 rounded-lg">
                                <div className="d-flex align-items-center mb-3">
                                  <h5 className="mb-0">Visit Site</h5>
                                </div>
                                <p className="card-text text-gray-500 mb-3">Clicks on "Visit Site".</p>
                                <div className="mb-2">
                                  <div className="d-flex justify-content-between mb-1">
                                    <span className="small">Logged In (LI)</span>
                                    <span className="event-badges px-2">{event.EVENT_SITE_CLICKS_LOGGED_IN ?? 0}</span>
                                  </div>
                                  <div className="d-flex justify-content-between mb-3">
                                    <span className="small">Not Logged In (NL)</span>
                                    <span className="event-badges px-2">{event.EVENT_SITE_CLICKS_NOT_LOGGED_IN ?? 0}</span>
                                  </div>
                                </div>
                                <div className="border-top pt-2">
                                  <div className="d-flex justify-content-between align-items-center">
                                    <span className="fw-bold">Total</span>
                                    <span className="h4 event-badges px-2">{(event.EVENT_SITE_CLICKS_LOGGED_IN ?? 0) + (event.EVENT_SITE_CLICKS_NOT_LOGGED_IN ?? 0)}</span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="col-lg-3 col-md-6">
                              <div className="event-sidebar p-4 h-100 rounded-lg">
                                <div className="d-flex align-items-center mb-3">
                                  <h5 className="mb-0">Get Tickets</h5>
                                </div>
                                <p className="card-text text-gray-500 mb-3">Clicks on "Get Tickets".</p>
                                <div className="mb-2">
                                  <div className="d-flex justify-content-between mb-1">
                                    <span className="small">Logged In (LI)</span>
                                    <span className="event-badges px-2">{event.EVENT_TICKET_SITE_CLICKS_LOGGED_IN ?? 0}</span>
                                  </div>
                                  <div className="d-flex justify-content-between mb-3">
                                    <span className="small">Not Logged In (NL)</span>
                                    <span className="event-badges px-2">{event.EVENT_TICKET_SITE_CLICKS_NOT_LOGGED_IN ?? 0}</span>
                                  </div>
                                </div>
                                <div className="border-top pt-2">
                                  <div className="d-flex justify-content-between align-items-center">
                                    <span className="fw-bold">Total</span>
                                    <span className="h4 event-badges px-2">{(event.EVENT_TICKET_SITE_CLICKS_LOGGED_IN ?? 0) + (event.EVENT_TICKET_SITE_CLICKS_NOT_LOGGED_IN ?? 0)}</span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="col-lg-3 col-md-6">
                              <div className="event-sidebar p-4 h-100 rounded-lg">
                                <div className="d-flex align-items-center mb-3">
                                  <h5 className="mb-0">Page Views</h5>
                                </div>
                                <p className="card-text text-gray-500 mb-3">Event page visits.</p>
                                <div className="mb-2">
                                  <div className="d-flex justify-content-between mb-1">
                                    <span className="small">External Link</span>
                                    <span className="event-badges px-2">{event.EVENT_LOADED_SITE_EXTERNAL ?? 0}</span>
                                  </div>
                                  <div className="d-flex justify-content-between mb-3">
                                    <span className="small">Internal Link</span>
                                    <span className="event-badges px-2">{event.EVENT_LOADED_SITE_FROM_PAGE ?? 0}</span>
                                  </div>
                                  <div className="d-flex justify-content-between mb-3">
                                    <span className="small">Clicked 'Share Event'</span>
                                    <span className="event-badges px-2">NL - {event.EVENT_SHARE_SITE_CLICKS_NOT_LOGGED_IN ?? 0} / LI - {event.EVENT_SHARE_SITE_CLICKS_LOGGED_IN ?? 0}</span>
                                  </div>
                                </div>
                                <div className="border-top pt-2">
                                  <div className="d-flex justify-content-between align-items-center">
                                    <span className="fw-bold">Total</span>
                                    <span className="h4 event-badges px-2">{(event.EVENT_LOADED_SITE_EXTERNAL ?? 0) + (event.EVENT_LOADED_SITE_FROM_PAGE ?? 0)}</span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="col-lg-3 col-md-6">
                              <div className="event-sidebar p-4 h-100 rounded-lg">
                                <div className="d-flex align-items-center mb-3">
                                  <h5 className="mb-0">Attending</h5>
                                </div>
                                <p className="card-text text-gray-500 mb-3">People attending via site.</p>

                                <div className="d-flex justify-content-between align-items-center mb-1">
                                    <span className="small">Total Person Attendees </span>
                                    <span className="mb-0 event-badges px-2 ">{event.ATTENDING_COUNT ?? 0}</span>
                                  </div>

                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                    <span className="small">Total Groups</span>
                                    <span className="mb-0 event-badges px-2 ">{Object.values(groupsAttendingTracking).length}</span>
                                  </div>

                                  <div className="d-flex justify-content-between align-items-center mb-1">
                                    <span className="small">Total Groups Members</span>
                                    <span className="mb-0 event-badges px-2 ">{(() => {
                                      return Object.values(groupsAttendingTracking)
                                        .filter((g) => g.GROUP_PUBLIC === "Y" && g.GROUP_MEMBER_COUNT_AT_JOIN !== null)
                                        .reduce((sum, g) => sum + g.GROUP_MEMBER_COUNT_AT_JOIN, 0);
                                    })()}</span>
                                  </div>
                                
                                  <div className="d-flex justify-content-between align-items-center mb-1">
                                    <span className="small">Total Attendees </span>
                                    <span className="mb-0 event-badges px-2 ">{(() => {
                                      const eventCount = Number(event.ATTENDING_COUNT) || 0;
                                      const groupMemberTotal = Object.values(groupsAttendingTracking)
                                        .filter((g) => g.GROUP_PUBLIC === "Y" && g.GROUP_MEMBER_COUNT_AT_JOIN !== null)
                                        .reduce((sum, g) => sum + g.GROUP_MEMBER_COUNT_AT_JOIN, 0);
                                      return eventCount + groupMemberTotal;
                                    })()}</span>
                                  </div>

                                </div>
                            </div>
                          </div>


                          <div className="row justify-content-between align-items-end m-3 mt-4 mb-0">
                            <div className="col-sm-12 text-end">
                                <button className="btn btn-primary" onClick={handleBackup}>Generate Backup</button>
                            </div>
                          </div>


                      </div>

                      <div className="tab-pane fade" id="nav-pricing" role="tabpanel" aria-labelledby="nav-pricing-tab">


                        <div className="">
                          
                          <div className="row justify-content-between align-items-end mt-5 mb-0">
                            <div className="col-sm-12 ">
                                <h2>Manage Pricing & Perks</h2>
                                <p className="card-text text-gray-500">Create custom pricing tiers and add perks for each ticket type (e.g., VIP lounge access, free parking).</p>
                            </div>
                          </div>


                          {/* Add New Pricing Tier */}
                        
                            <div className="mt-5">
                              <h6 className="card-title mb-3">Add New Pricing Tier</h6>
                              <div className="row">
                                <div className="col-md-4 mb-3">
                                  <label className="form-label">Tier Name</label>
                                  <input 
                                    type="text" 
                                    className="form-control" 
                                    placeholder="..."
                                    value={newPricingName}
                                    onChange={(e) => setNewPricingName(e.target.value)}
                                  />
                                </div>
                                <div className="col-md-2 mb-3">
                                  <label className="form-label">Minimum Price </label>
                                  <input 
                                    type="text" 
                                    className="form-control" 
                                    placeholder="0"
                                    value={newPricingMin}
                                    onChange={(e) => setNewPricingMin(e.target.value)}
                                  />
                                </div>
                                <div className="col-md-2 mb-3">
                                  <label className="form-label">Maximum Price </label>
                                  <input 
                                    type="text" 
                                    className="form-control" 
                                    placeholder="0"
                                    value={newPricingMax}
                                    onChange={(e) => setNewPricingMax(e.target.value)}
                                  />
                                </div>
                                <div className="col-md-2 mb-3">
                                  <label className="form-label">Most Popular</label>
                                  <select  
                                    className="form-select" 
                                    value={newBestSeller}
                                    onChange={(e) => setNewBestSeller(e.target.value)}
                                  >
                                    <option value="N">No</option>
                                    <option value="Y">Yes</option>
                                  </select>
                                </div>
                                <div className="col-md-auto d-flex align-items-end mb-3">
                                  <button
                                    className="btn btn-primary "
                                    onClick={handleAddPricing}
                                  >
                                    Add Tier
                                  </button>
                                </div>
                              </div>
                            </div>
                    
                        <div className="filter-section"></div>

                          {/* Existing Pricing Tiers */}
                          {pricingList && pricingList.length > 0 ? (
                            <div className="">
                              {pricingList.map((pricing) => (
                                <div key={pricing.PRICING_ID} className="card border-0 mb-4" style={{backgroundColor: 'var(--modal-bg)'}}>
                                  <div className="card-body">
                                    {/* Pricing Tier Info */}
                                    <div className="row mb-3">
                                      <div className="col-md-4 mb-2">
                                        <label className="form-label">Tier Name</label>
                                        {editingPricingId === pricing.PRICING_ID ? (
                                          <input 
                                            type="text" 
                                            className="form-control "
                                            value={editPricingName}
                                            onChange={(e) => setEditPricingName(e.target.value)}
                                          />
                                        ) : (
                                          <div className="fw-bold">{pricing.PRICING_NAME}</div>
                                        )}
                                      </div>
                                      <div className="col-md-2 mb-2">
                                        <label className="form-label">Minimum Price</label>
                                        {editingPricingId === pricing.PRICING_ID ? (
                                          <input 
                                            type="text" 
                                            className="form-control "
                                            value={editPricingMin}
                                            onChange={(e) => setEditPricingMin(e.target.value)}
                                          />
                                        ) : (
                                          <div>£{pricing.PRICING_PRICEMIN}</div>
                                        )}
                                      </div>
                                      <div className="col-md-2 mb-2">
                                        <label className="form-label">Maximum Price</label>
                                        {editingPricingId === pricing.PRICING_ID ? (
                                          <input 
                                            type="text" 
                                            className="form-control "
                                            value={editPricingMax}
                                            onChange={(e) => setEditPricingMax(e.target.value)}
                                          />
                                        ) : (
                                          <div>£{pricing.PRICING_PRICEMAX}</div>
                                        )}
                                      </div>

                                        <div className="col-md-2 mb-2">
                                        <label className="form-label">Most Popular</label>
                                        {editingPricingId === pricing.PRICING_ID ? (
                                          <select 
                                            className="form-select "
                                            value={editBestSeller}
                                            onChange={(e) => setEditBestSeller(e.target.value)}
                                          >
                                            <option value="N">No</option>
                                            <option value="Y">Yes</option>
                                          </select>
                                        ) : (
                                          <div>{pricing.PRICING_BESTSELLER === "Y" ? "Yes" : "No"}</div>
                                        )}
                                      </div>

                                      <div className="col-md-2 d-flex align-items-end mb-2">
                                        {editingPricingId === pricing.PRICING_ID ? (
                                          <>
                                            <button 
                                              className="btn btn-primary me-2"
                                              onClick={() => handleSavePricing(pricing.PRICING_ID)}
                                            >
                                             Save
                                            </button>
                                            <button 
                                              className="btn btn-outline-danger "
                                              onClick={() => setEditingPricingId(null)}
                                            >
                                               Cancel
                                            </button>
                                          </>
                                        ) : (
                                          <>
                                            <button 
                                              className="btn btn-primary me-2"
                                              onClick={() => handleEditPricing(pricing)}
                                            >
                                               Edit
                                            </button>
                                            <button 
                                              className="btn btn-sm btn-danger"
                                              onClick={() => handleDeletePricing(pricing.PRICING_ID)}
                                            >
                                              <i className="bi bi-trash3"></i> 
                                            </button>
                                          </>
                                        )}
                                      </div>
                                    </div>

                                    <hr className="my-3" />

                                    {/* Perks for this Pricing Tier */}
                                    <div className="mb-3">                                      
                                      {/* Add New Perk for this tier */}
                                      {newPerkPricingId !== pricing.PRICING_ID && (
                                        <button 
                                          className="btn btn-primary mb-3"
                                          onClick={() => setNewPerkPricingId(pricing.PRICING_ID)}
                                        >
                                          Add New Perk
                                        </button>
                                      )}

                                      {newPerkPricingId === pricing.PRICING_ID && (
                                        <div className="row">
                                          
                                          <div className="col-md-8">
                                          <input 
                                            type="text" 
                                            className="form-control" 
                                            placeholder="..."
                                            value={newPerkName}
                                            onChange={(e) => setNewPerkName(e.target.value)}
                                          />
                                          </div>

                                        <div className="col-md-2">
                                          <button 
                                            className="btn btn-primary"
                                            onClick={() => handleAddPerk(pricing.PRICING_ID)}
                                          >
                                           Add Perk
                                          </button>
                                       </div>

                                       <div className="col-md-2">
                                          <button 
                                            className="btn btn-outline-danger"
                                            onClick={() => {
                                              setNewPerkPricingId(null);
                                              setNewPerkName("");
                                            }}
                                          >
                                             Cancel
                                          </button>
                                        </div>

                                        </div>
                                      )}

                                      {/* Existing Perks for this tier */}
                                      {perksByPricingId[pricing.PRICING_ID] && perksByPricingId[pricing.PRICING_ID].length > 0 ? (
                                        <div className="list-group list-group-sm">
                                          {perksByPricingId[pricing.PRICING_ID].map((perk) => (
                                            <div key={perk.PERK_ID} className="list-group-item d-flex justify-content-between align-items-center py-2">
                                              <div className="flex-grow-1">
                                                {editingPerkPricingId === pricing.PRICING_ID && editingPerkId === perk.PERK_ID ? (
                                                  <input 
                                                    type="text" 
                                                    className="form-control "
                                                    value={editPerkName}
                                                    onChange={(e) => setEditPerkName(e.target.value)}
                                                  />
                                                ) : (
                                                  <><i className="bi bi-check-circle text-success me-2"></i>{perk.PERK_NAME}</>
                                                )}
                                              </div>
                                              <div>
                                                {editingPerkPricingId === pricing.PRICING_ID && editingPerkId === perk.PERK_ID ? (
                                                  <>
                                                    <button 
                                                      className="btn btn-primary me-2"
                                                      onClick={() => handleSavePerk(perk.PERK_ID, pricing.PRICING_ID)}
                                                    >
                                                      Save
                                                    </button>
                                                    <button 
                                                      className="btn btn-outline-danger"
                                                      onClick={() => {
                                                        setEditingPerkPricingId(null);
                                                        setEditingPerkId(null);
                                                      }}
                                                    >
                                                      Cancel
                                                    </button>
                                                  </>
                                                ) : (
                                                  <>
                                                    <button 
                                                      className="btn btn-primary me-2"
                                                      onClick={() => handleEditPerk(perk, pricing.PRICING_ID)}
                                                    >
                                                     Edit Perk
                                                    </button>
                                                    <button 
                                                      className="btn btn-danger"
                                                      onClick={() => handleDeletePerk(perk.PERK_ID, pricing.PRICING_ID)}
                                                    >
                                                      <i className="bi bi-trash3"></i>
                                                    </button>
                                                  </>
                                                )}
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      ) : (
                                        <></>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <>
                            </>
                          )}
                        </div>
                      </div>

                      <div className="tab-pane fade" id="nav-delete" role="tabpanel" aria-labelledby="nav-delete-tab">

                          <div className="mt-5">
                            <div className="row justify-content-between mb-3">

                              <div className="col-md-auto mb-3 text-start"> 
                                <h1> Delete Event </h1> 
                                <p className="card-text text-gray-500"> Warning clicking this button will delete the event - this action cannot be undone.</p> 
                              </div>
                               <div className="col-md-auto mb-3 text-end"> 
                                <button className="btn btn-danger mt-3" onClick={() => handleDeleteEvent(eventId)}> Delete Event </button>
                              </div>

                            </div>
                          </div>

                      </div>
                    </div>


   </div>

          </div>

          
        )}


      </div>

      {/* Join with Group Modal */}
      {showGroupModal && (
        <div className="modal fade show" id="joinGroupModal" tabIndex="-1" style={{ display: 'block' }} aria-hidden="false">
          <div className="modal-dialog ">
            <div className="modal-content ">
             
          
              <div className="modal-body">
                <div className="row justify-content-between align-items-start mb-0">
                  <div className="col-auto text-start">
                    <h3 className="modal-title">Manage Event in Groups</h3>
                  </div>
                  <div className="col-auto text-end"> 
                    <button type="button" className="close" data-bs-dismiss="modal" onClick={() => setShowGroupModal(false)}><i className="bi bi-x-lg"></i></button>
                  </div>
                </div>

                {userGroups.length > 0 ? (
                  <div>
                    <p className="mb-4 event-text small">Select a group to add or remove this event</p>
                    {userGroups.map((group) => {
                      const isInGroup = eventGroupAssociations[group.groupId];
                      return (
                        <div key={group.groupId} className="d-flex gap-2 mb-3 align-items-center bg-primary-dark p-2 rounded">
                          <span className="flex-grow-1">
                            <p className="mb-0">{group.GROUP_NAME}</p> 
                          </span>
                          {isInGroup ? (
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => handleRemoveEventFromGroup(group.groupId, group.GROUP_NAME)}
                              disabled={addingEventToGroup}
                              title="Remove event from group"
                            >
                              Leave
                            </button>
                          ) : (
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => handleAddEventToGroup(group)}
                              disabled={addingEventToGroup}
                              title="Add event to group"
                            >
                              Join
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p>You don't have any groups where you're an owner or admin.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Warning leaving site modal */}
      <div className="modal fade" id="leavingSiteModal" tabIndex="-1" aria-labelledby="leavingSiteModallabel" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content">
                
                <div className="modal-body">
                    
                     <div className="text-danger mb-3  ">
                     
                        <div className="d-flex justify-content-between align-items-starts">
                          <h4 className="bold mb-0">Warning! </h4> 
                          <button type="button" className="close" data-bs-dismiss="modal" aria-label="Close">
                            <i className="bi bi-x-lg"></i>
                          </button>
                        </div>

                      <p className="mb-0"> You're about to leave our site to go to an external site. </p>

                      </div>
                    
                      <p className="text-center mb-4"> 
                        <span className="mb-4 text-gray-400 text-center"> {event.EVENT_SITE} </span> <br/>
                        <span className="text-white mb-3"> is not part of our site.</span>
                      </p>

                    <p className="text-white text-center mb-5 small"> You're continuing at your own risk never enter your Attendiau password on an untrusted website. </p>

                    <div className="text-center ">
                      <a
                      href={event.EVENT_SITE}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-primary"
                      onClick={() => handleVisitSiteClick(event.EVENT_SITE)}
                      > 
                      Continue to external site 
                      </a>
                     </div>
                  
                </div>

            </div>
           </div>
      </div>

      {/* Warning leaving site modal tickets */}
      <div className="modal fade" id="leavingSiteTicketsModal" tabIndex="-1" aria-labelledby="leavingSiteTicketsModallabel" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content">
                
                <div className="modal-body">
                    
                     <div className="text-danger mb-3  ">
                     
                        <div className="d-flex justify-content-between align-items-starts">
                          <h4 className="bold mb-0">Warning! </h4> 
                          <button type="button" className="close" data-bs-dismiss="modal" aria-label="Close">
                            <i className="bi bi-x-lg"></i>
                          </button>
                        </div>

                      <p className="mb-0"> You're about to leave our site to go to an external site. </p>

                      </div>
                    
                      <p className="text-center mb-4"> 
                        <span className="mb-4 text-gray-400 text-center"> {event.EVENT_TICKETSITE} </span> <br/>
                        <span className="text-white mb-3"> is not part of our site.</span>
                      </p>

                    <p className="text-white text-center mb-5 small"> You're continuing at your own risk never enter your Attendiau password on an untrusted website. </p>

                    <div className="text-center">
                      <a
                        onClick={() => handleVisitTicketSiteClick(event.EVENT_TICKETSITE?.trim() || event.EVENT_SITE?.trim())}
                        className="btn btn btn-primary"
                        href={event.EVENT_TICKETSITE?.trim() || event.EVENT_SITE?.trim()}
                        target="_blank"
                        rel="noopener noreferrer"
                      > 
                      Continue to external site 
                      </a>
                     </div>
                  
                </div>

            </div>
           </div>
      </div>

      <Footer />
    </div>
  );
}

export default EventDetails;
